import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import InvoicesTab from "../components/projects/InvoicesTab";
import TimeAllocationTab from "../components/projects/TimeAllocationTab";
import MaterialRequestsTab from "../components/projects/MaterialRequestsTab";
import { SupplierAutocomplete } from "../components/SupplierAutocomplete";
import { DailyReports } from "../components/DailyReports";
import {
  HardHat, Plus, Loader2, X, Check, DollarSign, FileText, ClipboardList,
  Layers, TrendingUp, ChevronRight, Trash2, Send, AlertTriangle, Download, Mail, Copy,
  Sparkles, Image as ImageIcon, Wand2, Clock, Play, Square, UserCircle, Package,
} from "lucide-react";

const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none";

/** Safely extract a human-readable error message from an axios error.
 *  FastAPI 422 responses return `detail` as an array of objects — passing that
 *  directly to `toast.error(...)` crashes React ("Objects are not valid as a
 *  React child"). */
const errMsg = (e, fallback = "Failed") => {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((x) => (typeof x === "string" ? x : x?.msg || JSON.stringify(x))).join("; ");
  }
  if (typeof d === "object") return d.msg || JSON.stringify(d);
  return fallback;
};

const STATUS_UI = {
  draft:                     { label: "Draft",                 color: "bg-slate-100 text-slate-600 border-slate-200" },
  pending_acceptance:        { label: "Pending acceptance",    color: "bg-purple-50 text-purple-700 border-purple-200" },
  estimating:                { label: "Estimating",            color: "bg-amber-50 text-amber-700 border-amber-200" },
  pending_internal_approval: { label: "Internal approval",     color: "bg-yellow-50 text-yellow-800 border-yellow-200" },
  pending_client_approval:   { label: "Client approval",       color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  approved:                  { label: "Approved",              color: "bg-blue-50 text-blue-700 border-blue-200" },
  in_progress:               { label: "In progress",           color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  on_hold:                   { label: "On hold",               color: "bg-orange-50 text-orange-700 border-orange-200" },
  completed:                 { label: "Completed",             color: "bg-slate-200 text-slate-700 border-slate-300" },
  cancelled:                 { label: "Cancelled",             color: "bg-red-50 text-red-700 border-red-200" },
};

const PHASE_STATUS_UI = {
  not_started: { label: "Not started", color: "bg-slate-100 text-slate-500" },
  in_progress: { label: "In progress", color: "bg-emerald-50 text-emerald-700" },
  blocked:     { label: "Blocked",     color: "bg-red-50 text-red-700" },
  completed:   { label: "Done",        color: "bg-slate-200 text-slate-800" },
};

export default function ProjectsPage() {
  const { user } = useAuth();
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const canCreate = ["admin", "manager", "pm", "estimator"].includes(user.role);
  const canSeeAnalytics = ["admin", "manager", "pm", "estimator", "director", "financeiro", "analista"].includes(user.role);

  const load = async () => {
    setLoading(true);
    try {
      const [projects, kpi] = await Promise.all([
        api.get("/projects"),
        canSeeAnalytics
          ? api.get("/projects/proposals/analytics?days=90").catch(() => null)
          : Promise.resolve(null),
      ]);
      setRows(projects);
      setAnalytics(kpi);
    } catch { toast.error(t("error")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400">
            <HardHat className="w-4 h-4" /> Projects
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mt-1">Projects & Renovations</h1>
          <p className="hidden md:block text-sm text-slate-500 mt-1 max-w-2xl">
            Multi-phase construction/renovation jobs with change orders, progress billing and per-phase materials & subcontracts.
          </p>
        </div>
        {canCreate && (
          <button
            data-testid="prj-new-btn"
            onClick={() => setNewOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white text-sm font-semibold px-4 py-2 hover:bg-slate-800"
          >
            <Plus className="w-4 h-4" /> New project
          </button>
        )}
      </div>

      {analytics && analytics.sent_count > 0 && <ProposalAnalyticsBanner data={analytics} onOpen={(pid) => {
        const p = rows.find((r) => r.id === pid); if (p) setDetail(p);
      }} />}

      {loading && <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}
      {!loading && rows.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl py-16 text-center text-slate-400 text-sm">
          No projects yet. Create one to start tracking phases, materials and progress billing.
        </div>
      )}
      {!loading && rows.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="prj-list">
          <div className="hidden md:grid grid-cols-12 gap-3 items-center px-4 py-2 border-b border-slate-200 bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500">
            <div className="col-span-4">Project</div>
            <div className="col-span-2">Client · Site</div>
            <div className="col-span-1 text-right">Contract</div>
            <div className="col-span-1 text-right">Invoiced</div>
            <div className="col-span-2">Progress</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1 text-right">Updated</div>
          </div>
          <div className="divide-y divide-slate-100">
            {rows.map((p) => {
              const ui = STATUS_UI[p.status] || STATUS_UI.draft;
              const t2 = p.totals || {};
              const pct = Math.max(0, Math.min(100, t2.progress_pct || 0));
              return (
                <button
                  key={p.id}
                  data-testid={`prj-row-${p.id}`}
                  onClick={() => setDetail(p)}
                  className="w-full text-left hover:bg-slate-50 transition"
                >
                  {/* -------- Mobile card (< md) -------- */}
                  <div className="md:hidden p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] text-slate-400 font-medium tabular-nums">{p.number}</div>
                        <div className="text-sm font-semibold text-slate-900 truncate">{p.name}</div>
                        <div className="text-[11px] text-slate-500 truncate mt-0.5">
                          {p.client_name}{p.property_name ? ` · ${p.property_name}` : ""}
                        </div>
                      </div>
                      <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-full border shrink-0 ${ui.color} whitespace-nowrap`}>{ui.label}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px]">
                      <div>
                        <div className="text-[9px] uppercase text-slate-400 font-bold tracking-wide">Contract</div>
                        <div className="tabular-nums font-semibold text-slate-800">{money(t2.contract_effective)}</div>
                      </div>
                      <div>
                        <div className="text-[9px] uppercase text-slate-400 font-bold tracking-wide">Invoiced</div>
                        <div className="tabular-nums font-semibold text-emerald-700">{money(t2.invoiced)}</div>
                      </div>
                      <div>
                        <div className="text-[9px] uppercase text-slate-400 font-bold tracking-wide">Updated</div>
                        <div className="tabular-nums text-slate-500 text-[10px] mt-0.5">{(p.updated_at || p.created_at || "").slice(0, 10)}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-blue-500 to-emerald-500" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="text-[11px] tabular-nums font-semibold text-slate-700 w-9 text-right">{pct}%</div>
                    </div>
                  </div>

                  {/* -------- Desktop 12-col row (≥ md) -------- */}
                  <div className="hidden md:grid grid-cols-12 gap-3 items-center px-4 py-3">
                    <div className="col-span-4 min-w-0">
                      <div className="text-[10px] text-slate-400 font-medium tabular-nums">{p.number}</div>
                      <div className="text-sm font-semibold text-slate-900 truncate">{p.name}</div>
                    </div>
                    <div className="col-span-2 min-w-0 text-xs text-slate-500 truncate">
                      <div className="truncate font-medium text-slate-700">{p.client_name}</div>
                      <div className="truncate text-[11px]">{p.property_name || "—"}</div>
                    </div>
                    <div className="col-span-1 text-right text-xs tabular-nums font-semibold text-slate-900">{money(t2.contract_effective)}</div>
                    <div className="col-span-1 text-right text-xs tabular-nums text-emerald-600">{money(t2.invoiced)}</div>
                    <div className="col-span-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-blue-500 to-emerald-500" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="text-[11px] tabular-nums font-semibold text-slate-700 w-9 text-right">{pct}%</div>
                      </div>
                    </div>
                    <div className="col-span-1">
                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${ui.color} whitespace-nowrap`}>{ui.label}</span>
                    </div>
                    <div className="col-span-1 text-right text-[11px] text-slate-400 tabular-nums truncate">{(p.updated_at || p.created_at || "").slice(0, 10)}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {detail && (
        <ProjectDetailModal
          project={detail}
          user={user}
          onClose={() => setDetail(null)}
          onChange={async () => {
            await load();
            const fresh = await api.get(`/projects/${detail.id}`).catch(() => null);
            if (fresh) setDetail(fresh);
          }}
        />
      )}
      {newOpen && (
        <NewProjectModal
          onClose={() => setNewOpen(false)}
          onCreated={(p) => { setNewOpen(false); load(); setDetail(p); }}
        />
      )}
    </div>
  );
}

const Metric = ({ label, value, color = "text-slate-800" }) => (
  <div>
    <div className="text-[10px] uppercase font-bold text-slate-400">{label}</div>
    <div className={`font-semibold tabular-nums ${color}`}>{value}</div>
  </div>
);

/* ================= Proposal Analytics KPI Banner ================= */
function ProposalAnalyticsBanner({ data, onOpen }) {
  const [expanded, setExpanded] = useState(false);
  const rate = data.acceptance_rate || 0;
  const rateColor = rate >= 60 ? "text-emerald-600" : rate >= 30 ? "text-amber-600" : "text-red-600";
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="prj-analytics">
      <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-slate-200">
        <div className="p-3">
          <div className="text-[10px] uppercase font-bold text-slate-400">Sent (90d)</div>
          <div className="text-xl font-bold tabular-nums text-slate-900">{data.sent_count}</div>
          <div className="text-[11px] text-slate-500 tabular-nums">{money(data.sent_value)}</div>
        </div>
        <div className="p-3">
          <div className="text-[10px] uppercase font-bold text-slate-400">Accepted</div>
          <div className="text-xl font-bold tabular-nums text-emerald-700">{data.accepted_count}</div>
          <div className="text-[11px] text-emerald-600 tabular-nums">{money(data.accepted_value)}</div>
        </div>
        <div className="p-3">
          <div className="text-[10px] uppercase font-bold text-slate-400">Acceptance</div>
          <div className={`text-xl font-bold tabular-nums ${rateColor}`}>{rate}%</div>
          <div className="text-[11px] text-slate-500">win rate</div>
        </div>
        <div className="p-3">
          <div className="text-[10px] uppercase font-bold text-slate-400">Avg time to accept</div>
          <div className="text-xl font-bold tabular-nums text-slate-900">{data.avg_days_to_accept || 0}<span className="text-xs font-medium text-slate-400 ml-1">days</span></div>
          <div className="text-[11px] text-slate-500">from sent → signed</div>
        </div>
        <div className="p-3">
          <div className="text-[10px] uppercase font-bold text-slate-400">Awaiting</div>
          <div className={`text-xl font-bold tabular-nums ${data.stale.length > 0 ? "text-red-600" : "text-slate-900"}`}>{data.awaiting_count}</div>
          <div className="text-[11px] text-slate-500">
            {data.stale.length > 0 ? <span className="text-red-600 font-semibold">{data.stale.length} stale &gt;7d</span> : "on track"}
          </div>
        </div>
      </div>
      {data.stale.length > 0 && (
        <div className="border-t border-slate-200 bg-red-50/40">
          <button
            data-testid="prj-analytics-toggle"
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-red-700 hover:bg-red-50"
          >
            <span className="flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5" /> {data.stale.length} proposal(s) need follow-up</span>
            <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? "rotate-90" : ""}`} />
          </button>
          {expanded && (
            <div className="border-t border-red-100 divide-y divide-red-100">
              {data.stale.map((s) => (
                <button
                  key={s.id}
                  data-testid={`prj-stale-${s.id}`}
                  onClick={() => onOpen(s.id)}
                  className="w-full flex items-center justify-between px-4 py-2 text-xs text-left hover:bg-red-50"
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{s.number} · {s.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {s.client_name} · sent to {s.sent_to} · {s.age_days}d ago
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <div className="tabular-nums font-semibold text-slate-900">{money(s.contract_value)}</div>
                    <div className={`text-[10px] font-bold ${s.expired ? "text-red-600" : "text-amber-600"}`}>
                      {s.expired ? "EXPIRED" : `${s.days_until_expiry}d left`}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ================= Registry pickers (Client + Property) ================= */
function useRegistryList(path) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const reload = async () => {
    setLoading(true);
    try { setItems(await api.get(path)); } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [path]);
  return { items, loading, reload };
}

function ClientPicker({ value, onChange, testid = "prj-client-picker" }) {
  const { items, reload } = useRegistryList("/clients");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [addingOpen, setAddingOpen] = useState(false);

  useEffect(() => {
    if (value?.id) {
      const found = items.find((c) => c.id === value.id);
      if (found) setQ(found.name);
    }
  }, [value, items]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items.slice(0, 12);
    return items.filter((c) => c.name.toLowerCase().includes(s) || (c.contact_name || "").toLowerCase().includes(s)).slice(0, 12);
  }, [q, items]);

  const select = (c) => { onChange({ id: c.id, name: c.name, email: c.email, phone: c.phone }); setQ(c.name); setOpen(false); };
  const clear = () => { onChange({ id: null, name: "" }); setQ(""); };

  return (
    <div className="relative">
      <div className="flex gap-1">
        <input
          data-testid={testid}
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); if (value?.id) onChange({ id: null, name: e.target.value }); }}
          placeholder="Search registered client..."
          className={inputCls}
        />
        {value?.id && <button type="button" onClick={clear} className="text-xs px-2 rounded border border-slate-200 hover:bg-slate-50">×</button>}
      </div>
      {open && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {filtered.length === 0 && <div className="p-2.5 text-xs text-slate-400">No client matches.</div>}
          {filtered.map((c) => (
            <button
              key={c.id} type="button"
              data-testid={`${testid}-opt-${c.id}`}
              onClick={() => select(c)}
              className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 border-b border-slate-100 last:border-0"
            >
              <div className="font-semibold text-slate-900">{c.name}</div>
              <div className="text-slate-400">{c.contact_name || "—"} · {c.email || "no email"}</div>
            </button>
          ))}
          <button
            type="button"
            data-testid={`${testid}-add`}
            onClick={() => { setAddingOpen(true); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-xs font-semibold text-blue-600 hover:bg-blue-50 bg-blue-50/40"
          >
            <Plus className="w-3 h-3 inline mr-1" /> Register new client {q ? `"${q}"` : ""}
          </button>
        </div>
      )}
      {open && <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />}
      {addingOpen && (
        <QuickClientModal
          initialName={q}
          onClose={() => setAddingOpen(false)}
          onCreated={async (c) => { await reload(); select(c); setAddingOpen(false); }}
        />
      )}
    </div>
  );
}

function QuickClientModal({ initialName, onClose, onCreated }) {
  const [form, setForm] = useState({ name: initialName || "", contact_name: "", email: "", phone: "", address: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.name.trim()) { toast.error("Client name required"); return; }
    setBusy(true);
    try {
      const c = await api.post("/clients", form);
      toast.success("Client registered");
      onCreated(c);
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="prj-quick-client-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="font-semibold">Register client</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div><label className="text-[10px] font-bold uppercase text-slate-500">Company / client name *</label>
            <input data-testid="prj-quick-client-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} autoFocus /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[10px] font-bold uppercase text-slate-500">Contact person</label>
              <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className={inputCls} /></div>
            <div><label className="text-[10px] font-bold uppercase text-slate-500">Phone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} /></div>
          </div>
          <div><label className="text-[10px] font-bold uppercase text-slate-500">Email</label>
            <input data-testid="prj-quick-client-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="Used for sending proposals" /></div>
          <div><label className="text-[10px] font-bold uppercase text-slate-500">Address</label>
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} /></div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button onClick={onClose} disabled={busy} className="rounded px-3 py-1.5 text-sm border border-slate-300 hover:bg-slate-50">Cancel</button>
          <button data-testid="prj-quick-client-save" onClick={save} disabled={busy} className="rounded px-3 py-1.5 text-sm bg-slate-900 text-white font-semibold disabled:opacity-50 inline-flex items-center gap-2">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Register
          </button>
        </div>
      </div>
    </div>
  );
}

function PropertyPicker({ value, clientId, onChange, testid = "prj-property-picker" }) {
  const { items, reload } = useRegistryList("/properties");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [addingOpen, setAddingOpen] = useState(false);

  useEffect(() => {
    if (value?.id) {
      const found = items.find((p) => p.id === value.id);
      if (found) setQ(found.name);
    }
  }, [value, items]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let list = items;
    if (clientId) list = list.filter((p) => !p.client_id || p.client_id === clientId);
    if (!s) return list.slice(0, 12);
    return list.filter((p) => p.name.toLowerCase().includes(s) || (p.address || "").toLowerCase().includes(s)).slice(0, 12);
  }, [q, items, clientId]);

  const select = (p) => { onChange({ id: p.id, name: p.name, address: p.address }); setQ(p.name); setOpen(false); };
  const clear = () => { onChange({ id: null, name: "" }); setQ(""); };

  return (
    <div className="relative">
      <div className="flex gap-1">
        <input
          data-testid={testid}
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); if (value?.id) onChange({ id: null, name: e.target.value }); }}
          placeholder="Search registered property..."
          className={inputCls}
        />
        {value?.id && <button type="button" onClick={clear} className="text-xs px-2 rounded border border-slate-200 hover:bg-slate-50">×</button>}
      </div>
      {open && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {filtered.length === 0 && <div className="p-2.5 text-xs text-slate-400">No property matches.</div>}
          {filtered.map((p) => (
            <button
              key={p.id} type="button"
              data-testid={`${testid}-opt-${p.id}`}
              onClick={() => select(p)}
              className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 border-b border-slate-100 last:border-0"
            >
              <div className="font-semibold text-slate-900">{p.name}</div>
              <div className="text-slate-400">{p.address || "—"} · {p.units_count || 0} unit(s)</div>
            </button>
          ))}
          <button
            type="button"
            data-testid={`${testid}-add`}
            onClick={() => { setAddingOpen(true); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-xs font-semibold text-blue-600 hover:bg-blue-50 bg-blue-50/40"
          >
            <Plus className="w-3 h-3 inline mr-1" /> Register new property {q ? `"${q}"` : ""}
          </button>
        </div>
      )}
      {open && <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />}
      {addingOpen && (
        <QuickPropertyModal
          initialName={q}
          clientId={clientId}
          onClose={() => setAddingOpen(false)}
          onCreated={async (p) => { await reload(); select(p); setAddingOpen(false); }}
        />
      )}
    </div>
  );
}

function QuickPropertyModal({ initialName, clientId, onClose, onCreated }) {
  const [form, setForm] = useState({ name: initialName || "", address: "", city: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.name.trim() || !form.address.trim()) { toast.error("Name and address required"); return; }
    setBusy(true);
    try {
      const p = await api.post("/properties", { ...form, client_id: clientId || null });
      toast.success("Property registered");
      onCreated(p);
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="prj-quick-property-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="font-semibold">Register property</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div><label className="text-[10px] font-bold uppercase text-slate-500">Property name *</label>
            <input data-testid="prj-quick-property-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} autoFocus placeholder="Building A / 26 Sky Lane" /></div>
          <div><label className="text-[10px] font-bold uppercase text-slate-500">Address *</label>
            <input data-testid="prj-quick-property-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} placeholder="Street, City, State" /></div>
          <div><label className="text-[10px] font-bold uppercase text-slate-500">City</label>
            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} /></div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button onClick={onClose} disabled={busy} className="rounded px-3 py-1.5 text-sm border border-slate-300 hover:bg-slate-50">Cancel</button>
          <button data-testid="prj-quick-property-save" onClick={save} disabled={busy} className="rounded px-3 py-1.5 text-sm bg-slate-900 text-white font-semibold disabled:opacity-50 inline-flex items-center gap-2">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Register
          </button>
        </div>
      </div>
    </div>
  );
}

/* ================= AI Assist (scope generator) ================= */
function AIAssistSection({ onApply, currentScopes, onClearScopes }) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState([]);   // array of data URLs
  const [tradesHint, setTradesHint] = useState("");
  const [targetBudget, setTargetBudget] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);   // { scopes, suggested_contract_value, computed_total_from_steps, assumptions }

  const readFile = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = () => rej(r.error);
    r.readAsDataURL(file);
  });

  const addPhotos = async (files) => {
    if (photos.length + files.length > 6) { toast.error("Max 6 photos"); return; }
    const urls = await Promise.all([...files].map(readFile));
    setPhotos([...photos, ...urls]);
  };

  const removePhoto = (i) => setPhotos(photos.filter((_, j) => j !== i));

  const generate = async () => {
    if (!description.trim() && photos.length === 0) {
      toast.error("Add a description or at least one photo");
      return;
    }
    setLoading(true);
    setPreview(null);
    try {
      const res = await api.post("/projects/ai-generate-scopes", {
        description: description.trim(),
        photos,
        trades_hint: tradesHint || null,
        target_budget: targetBudget ? parseFloat(targetBudget) : null,
      });
      setPreview(res);
      if (!res.scopes || res.scopes.length === 0) {
        toast.error("AI returned no scopes — try a more detailed description");
      } else {
        // Auto-apply immediately so the phases/scopes are staged into the
        // project even if the user forgets to click the confirm button.
        onApply({
          phases: res.phases || [],
          scopes: res.scopes,
          suggested_contract_value: res.suggested_contract_value,
          description: description.trim(),
        });
        toast.success(`${res.scopes.length} fase(s) aplicadas ao projeto — pode editar abaixo antes de criar`);
      }
    } catch (e) {
      toast.error(errMsg(e, "AI generation failed"));
    } finally { setLoading(false); }
  };

  // Any edit to the preview state should keep the parent's staged scopes/phases in sync.
  useEffect(() => {
    if (!preview) return;
    const recomputed = preview.scopes.reduce((a, s) => a + scopeTotal(s), 0);
    onApply({
      phases: preview.phases || [],
      scopes: preview.scopes,
      suggested_contract_value: Math.round(recomputed * 100) / 100 || preview.suggested_contract_value,
      description: description.trim(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  const scopeTotal = (sc) => {
    const steps = (sc.steps || []).reduce((s, x) => s + (x.quantity || 0) * (x.unit_price || 0), 0);
    const sub = sc.subcontract ? (sc.subcontract.billed_to_client || 0) : 0;
    return steps + sub;
  };

  // Editable step row inside the AI preview
  const renderEditStep = (st, scopeId, patchStep, removeStep) => (
    <div key={st.id} className="grid grid-cols-12 gap-1 items-center py-0.5" data-testid={`prj-ai-step-${st.id}`}>
      <input
        placeholder="Description"
        value={st.description || ""}
        onChange={(e) => patchStep(scopeId, st.id, { description: e.target.value })}
        className="col-span-5 rounded border border-slate-200 px-1.5 py-0.5 text-[11px]"
      />
      <input type="number" min="0" step="any" value={st.quantity || 0}
        onChange={(e) => patchStep(scopeId, st.id, { quantity: parseFloat(e.target.value) || 0 })}
        className="col-span-1 rounded border border-slate-200 px-1 py-0.5 text-[11px] text-right" />
      <input placeholder="un" value={st.unit || ""}
        onChange={(e) => patchStep(scopeId, st.id, { unit: e.target.value })}
        className="col-span-1 rounded border border-slate-200 px-1 py-0.5 text-[11px]" />
      <input type="number" min="0" step="0.01" value={st.unit_price || 0}
        onChange={(e) => patchStep(scopeId, st.id, { unit_price: parseFloat(e.target.value) || 0 })}
        className="col-span-2 rounded border border-slate-200 px-1 py-0.5 text-[11px] text-right" />
      <div className="col-span-2 text-[11px] tabular-nums font-semibold text-right">
        {money((st.quantity || 0) * (st.unit_price || 0))}
      </div>
      <button
        onClick={() => removeStep(scopeId, st.id)}
        data-testid={`prj-ai-step-remove-${st.id}`}
        className="col-span-1 p-0.5 text-slate-400 hover:text-red-600 justify-self-end"
      ><Trash2 className="w-3 h-3" /></button>
    </div>
  );

  return (
    <div className="border-2 border-dashed border-purple-200 bg-gradient-to-br from-purple-50/60 to-blue-50/60 rounded-xl overflow-hidden" data-testid="prj-ai-assist">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="prj-ai-toggle"
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-purple-50/50 transition"
      >
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-900">AI Estimate Assistant</div>
            <div className="text-[11px] text-slate-500">Describe the project or upload photos — get suggested scopes & prices</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {currentScopes.length > 0 && (
            <span className="text-[10px] font-bold uppercase bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
              {currentScopes.length} scope{currentScopes.length > 1 ? "s" : ""} applied
            </span>
          )}
          <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
        </div>
      </button>

      {open && (
        <div className="border-t border-purple-200 p-4 space-y-3 bg-white/70">
          <div>
            <label className="text-[10px] font-bold uppercase text-slate-500">Describe the job / scope of work</label>
            <textarea
              data-testid="prj-ai-description"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Renovate 2-bedroom apartment: replace drywall in living room, laminate flooring 400 sqft, paint all rooms, replace kitchen sink + one bath vanity, GFCI outlets in wet areas..."
              className={`${inputCls} text-xs leading-relaxed`}
            />
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase text-slate-500">Photos / plans (optional, max 6)</label>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-1">
              {photos.map((p, i) => (
                <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                  <img src={p} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    data-testid={`prj-ai-photo-remove-${i}`}
                    className="absolute top-1 right-1 p-1 bg-white/90 hover:bg-red-500 hover:text-white rounded-full opacity-0 group-hover:opacity-100 transition"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {photos.length < 6 && (
                <label className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-purple-400 hover:text-purple-600 cursor-pointer">
                  <input
                    type="file" accept="image/*" multiple
                    data-testid="prj-ai-photo-input"
                    onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }}
                    className="hidden"
                  />
                  <ImageIcon className="w-5 h-5 mb-0.5" />
                  <span className="text-[10px] font-semibold">Add photo</span>
                </label>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500">Trade hint (optional)</label>
              <input
                data-testid="prj-ai-trades"
                value={tradesHint}
                onChange={(e) => setTradesHint(e.target.value)}
                placeholder="e.g. electrical, plumbing"
                className={`${inputCls} text-xs`}
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500">Target budget (optional)</label>
              <input
                data-testid="prj-ai-budget"
                type="number"
                step="100"
                value={targetBudget}
                onChange={(e) => setTargetBudget(e.target.value)}
                placeholder="25000"
                className={`${inputCls} text-xs tabular-nums`}
              />
            </div>
          </div>

          <button
            type="button"
            data-testid="prj-ai-generate"
            onClick={generate}
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white text-sm font-bold px-4 py-3 shadow-sm disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {loading ? "Analyzing project..." : "Generate scopes with AI"}
          </button>

          {preview && (
            <div className="mt-3 space-y-2" data-testid="prj-ai-preview">
              <div className="flex items-center justify-between gap-3 flex-wrap bg-slate-900 text-white rounded-lg p-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-300 font-bold">AI Suggestion</div>
                  <div className="text-xs text-slate-300">{preview.scopes.length} scopes · {preview.photos_used || 0} photo(s) analyzed</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase text-slate-300">Suggested contract</div>
                  <div className="text-xl font-bold tabular-nums">{money(preview.suggested_contract_value)}</div>
                </div>
              </div>

              {preview.assumptions && (
                <div className="text-[11px] text-slate-600 italic border-l-2 border-amber-400 pl-2.5 py-1 bg-amber-50/60 rounded-r">
                  <b>Assumptions:</b> {preview.assumptions}
                </div>
              )}

              <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                {(preview.phases || []).map((ph, pi) => {
                  const phaseScopes = preview.scopes.filter((s) => s.phase_id === ph.id);
                  const phaseTotal = phaseScopes.reduce((a, s) => a + scopeTotal(s), 0);

                  const removePhase = () => {
                    if (!window.confirm(`Remove phase "${ph.name}"?`)) return;
                    setPreview({
                      ...preview,
                      phases: preview.phases.filter((p) => p.id !== ph.id),
                      scopes: preview.scopes.filter((s) => s.phase_id !== ph.id),
                    });
                  };
                  const patchStep = (scopeId, stepId, patch) => {
                    setPreview({
                      ...preview,
                      scopes: preview.scopes.map((s) => s.id !== scopeId ? s : {
                        ...s,
                        steps: s.steps.map((st) => st.id === stepId ? { ...st, ...patch } : st),
                      }),
                    });
                  };
                  const removeStep = (scopeId, stepId) => {
                    setPreview({
                      ...preview,
                      scopes: preview.scopes.map((s) => s.id !== scopeId ? s : {
                        ...s, steps: s.steps.filter((st) => st.id !== stepId),
                      }),
                    });
                  };
                  const addStep = (scopeId, kind) => {
                    const newSt = {
                      id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                      description: "", quantity: 1,
                      unit: kind === "material" ? "EA" : "hr",
                      unit_price: 0, kind, completed_pct: 0,
                    };
                    setPreview({
                      ...preview,
                      scopes: preview.scopes.map((s) => s.id !== scopeId ? s : {
                        ...s, steps: [...(s.steps || []), newSt],
                      }),
                    });
                  };

                  return (
                    <div key={ph.id} className="border border-purple-200 rounded-lg overflow-hidden bg-white" data-testid={`prj-ai-phase-${pi}`}>
                      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 py-1.5 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="text-[10px] font-bold bg-white/20 rounded px-1.5 py-0.5 shrink-0">FASE {ph.order || pi + 1}</span>
                          <input
                            data-testid={`prj-ai-phase-name-${pi}`}
                            value={ph.name}
                            onChange={(e) => setPreview({
                              ...preview,
                              phases: preview.phases.map((p) => p.id === ph.id ? { ...p, name: e.target.value } : p),
                              scopes: preview.scopes.map((s) => s.phase_id === ph.id ? { ...s, name: e.target.value } : s),
                            })}
                            className="bg-white/10 border border-white/20 rounded px-1.5 py-0.5 text-xs font-bold text-white min-w-0 flex-1"
                          />
                        </div>
                        <span className="text-xs font-bold tabular-nums shrink-0">{money(phaseTotal)}</span>
                        <button
                          data-testid={`prj-ai-phase-remove-${pi}`}
                          onClick={removePhase}
                          className="p-0.5 text-white/70 hover:text-red-200"
                        ><Trash2 className="w-3 h-3" /></button>
                      </div>
                      <div className="p-2 space-y-2">
                        {phaseScopes.map((sc) => {
                          const laborSteps = (sc.steps || []).filter((s) => (s.kind || "labor") === "labor");
                          const materialSteps = (sc.steps || []).filter((s) => s.kind === "material");
                          return (
                            <div key={sc.id}>
                              {laborSteps.length > 0 && (
                                <div className="text-[9px] font-bold uppercase text-blue-800 tracking-wider mb-1">
                                  <i className="fa-solid fa-hammer mr-1"></i> Labor ({laborSteps.length})
                                </div>
                              )}
                              {laborSteps.map((st) => renderEditStep(st, sc.id, patchStep, removeStep))}
                              <button
                                data-testid={`prj-ai-add-labor-${sc.id}`}
                                onClick={() => addStep(sc.id, "labor")}
                                className="text-[10px] font-semibold text-blue-600 hover:text-blue-800 inline-flex items-center gap-0.5 mt-0.5 mb-1"
                              ><Plus className="w-2.5 h-2.5" /> Add labor step</button>

                              {materialSteps.length > 0 && (
                                <div className="text-[9px] font-bold uppercase text-amber-800 tracking-wider mb-1 mt-1">
                                  <i className="fa-solid fa-box mr-1"></i> Material ({materialSteps.length})
                                </div>
                              )}
                              {materialSteps.map((st) => renderEditStep(st, sc.id, patchStep, removeStep))}
                              <button
                                data-testid={`prj-ai-add-material-${sc.id}`}
                                onClick={() => addStep(sc.id, "material")}
                                className="text-[10px] font-semibold text-amber-600 hover:text-amber-800 inline-flex items-center gap-0.5 mt-0.5 mb-1"
                              ><Plus className="w-2.5 h-2.5" /> Add material step</button>

                              {sc.subcontract?.subcontractor_name && (
                                <div className="text-[10px] text-slate-400 italic mt-1 pt-1 border-t border-slate-100">
                                  <i className="fa-solid fa-hammer mr-1"></i> Suggested sub: {sc.subcontract.subcontractor_name}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {/* Add new phase button */}
                <button
                  data-testid="prj-ai-add-phase"
                  onClick={() => {
                    const name = window.prompt("Name of the new phase?");
                    if (!name) return;
                    const phId = `tmp-ph-${Date.now()}`;
                    const scId = `tmp-sc-${Date.now()}`;
                    setPreview({
                      ...preview,
                      phases: [...preview.phases, {
                        id: phId, name, order: preview.phases.length + 1,
                        budget_labor: 0, budget_material: 0, budget_subs: 0,
                        progress_pct: 0, status: "planned",
                      }],
                      scopes: [...preview.scopes, {
                        id: scId, name, trade: "", phase_id: phId, order: 0,
                        steps: [], subcontract: null,
                      }],
                    });
                  }}
                  className="w-full text-xs font-semibold text-purple-700 hover:text-purple-900 inline-flex items-center justify-center gap-1 border-2 border-dashed border-purple-300 rounded-lg py-2 hover:bg-purple-50"
                ><Plus className="w-3.5 h-3.5" /> Add new phase</button>
              </div>

              <div className="flex gap-2 pt-1">
                <div className="flex-1 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs text-emerald-800 font-semibold inline-flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  {preview.scopes.length} phase{preview.scopes.length !== 1 ? "s" : ""} applied — you can edit above or click &quot;Create project&quot;
                </div>
                <button
                  type="button"
                  data-testid="prj-ai-discard"
                  onClick={() => { setPreview(null); onClearScopes(); }}
                  className="rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-3"
                >
                  Discard
                </button>
              </div>
            </div>
          )}

          {currentScopes.length > 0 && !preview && (
            <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-xs">
              <div className="text-emerald-700 font-semibold">
                ✓ {currentScopes.length} scope{currentScopes.length > 1 ? "s" : ""} will be created with the project
              </div>
              <button type="button" onClick={onClearScopes} className="text-red-600 hover:underline font-semibold text-[11px]">Clear</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ================= New project modal ================= */
function useUsersByRole(role) {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get(`/users?role=${encodeURIComponent(role)}`)
      .then((r) => { if (!cancelled) setUsers(r || []); })
      .catch(() => { if (!cancelled) setUsers([]); });
    return () => { cancelled = true; };
  }, [role]);
  return users;
}

/* =========== AI Assist Modal — apply to EXISTING project =========== */
function ProjectAIAssistModal({ project, onClose, onApplied }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // AI-generated bundle awaiting apply
  const [clearExisting, setClearExisting] = useState(false);

  const apply = async () => {
    if (!preview?.scopes?.length && !preview?.phases?.length) {
      toast.error("Nothing to apply yet — generate first");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/append-scopes`, {
        phases: preview.phases || [],
        scopes: preview.scopes || [],
        clear_existing: clearExisting,
      });
      toast.success(`${(preview.phases || []).length} phase(s) & ${(preview.scopes || []).length} scope(s) applied`);
      onApplied();
    } catch (e) {
      toast.error(errMsg(e, "Failed to apply"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="prj-ai-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-semibold inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-600" /> Generate WBS with AI
            </h2>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Add AI-generated phases &amp; scopes to <b>{project.name}</b> ({project.number}).
              {(project.phases || []).length > 0 && (
                <label className="ml-3 inline-flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={clearExisting} onChange={(e) => setClearExisting(e.target.checked)}
                    data-testid="prj-ai-clear-existing" className="rounded" />
                  <span className="text-rose-600 font-semibold">Replace existing ({project.phases.length})</span>
                </label>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4">
          <AIAssistSection
            onApply={(bundle) => setPreview(bundle)}
            currentScopes={preview?.scopes || []}
            onClearScopes={() => setPreview(null)}
          />
        </div>
        {preview && (preview.phases?.length > 0 || preview.scopes?.length > 0) && (
          <div className="flex justify-between items-center px-5 py-3 border-t border-slate-200 sticky bottom-0 bg-white gap-3">
            <div className="text-xs text-slate-600">
              Ready to apply: <b>{(preview.phases || []).length}</b> phase(s) · <b>{(preview.scopes || []).length}</b> scope(s)
              {clearExisting && <span className="ml-2 text-rose-600 font-semibold">(will replace existing)</span>}
            </div>
            <div className="flex gap-2">
              <button onClick={onClose} disabled={busy} className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50">Cancel</button>
              <button
                data-testid="prj-ai-apply-btn"
                onClick={apply} disabled={busy}
                className="text-xs px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Apply to project
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function NewProjectModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", contract_value: 0,
    start_date: "", end_date: "", notes: "",
    pm_id: "", pm_name: "",
    analyst_id: "", analyst_name: "",
    labor_markup_pct: 0, material_markup_pct: 0, overall_markup_pct: 0,
  });
  const [client, setClient] = useState({ id: null, name: "" });
  const [property, setProperty] = useState({ id: null, name: "" });
  const [phases, setPhases] = useState([{ name: "Phase 1", order: 1, budget_labor: 0, budget_material: 0, budget_subs: 0 }]);
  const [scopes, setScopes] = useState([]);
  const [busy, setBusy] = useState(false);
  const pms = useUsersByRole("pm");
  const analysts = useUsersByRole("analista");

  const addPhase = () => setPhases([...phases, { name: `Phase ${phases.length + 1}`, order: phases.length + 1, budget_labor: 0, budget_material: 0, budget_subs: 0 }]);
  const patchPhase = (i, p) => setPhases(phases.map((x, j) => j === i ? { ...x, ...p } : x));
  const removePhase = (i) => setPhases(phases.filter((_, j) => j !== i));

  const applyAiScopes = ({ phases: aiPhases, scopes: aiScopes, description }) => {
    setScopes(aiScopes);
    if (aiPhases && aiPhases.length > 0) {
      setPhases(aiPhases.map((p, i) => ({ ...p, order: p.order || i + 1 })));
    }
    setForm((f) => ({
      ...f,
      // We DO NOT copy the AI's suggested_contract_value here anymore — it is the COST
      // total (SUM of step qty × unit_price). The Contract $ field is now auto-driven
      // by the Pricing summary below (Cost × markup = Sell), so the client-facing price
      // always has the markup baked in.
      notes: description || f.notes,
    }));
  };

  // Live pricing summary — Cost (sum of steps) → Sell (with markup) → Margin
  const pricing = useMemo(() => {
    let laborCost = 0, materialCost = 0, subsCost = 0;
    (scopes || []).forEach((sc) => {
      (sc.steps || []).forEach((s) => {
        const amt = (parseFloat(s.quantity) || 0) * (parseFloat(s.unit_price) || 0);
        if ((s.kind || "labor") === "labor") laborCost += amt;
        else materialCost += amt;
      });
      const subsList = sc.subcontracts?.length ? sc.subcontracts : (sc.subcontract ? [sc.subcontract] : []);
      subsList.forEach((sub) => { subsCost += parseFloat(sub?.contract_amount) || 0; });
    });
    const lmk = parseFloat(form.labor_markup_pct) || 0;
    const mmk = parseFloat(form.material_markup_pct) || 0;
    const omk = parseFloat(form.overall_markup_pct) || 0;
    const laborSell = laborCost * (1 + lmk / 100);
    const materialSell = materialCost * (1 + mmk / 100);
    const subsSell = subsCost;   // subs already have billed_to_client separately; no additional markup here
    const subtotal = laborSell + materialSell + subsSell;
    const sellTotal = subtotal * (1 + omk / 100);
    const cost = laborCost + materialCost + subsCost;
    const margin = sellTotal - cost;
    const marginPct = sellTotal > 0 ? (margin / sellTotal) * 100 : 0;
    return { laborCost, materialCost, subsCost, cost, laborSell, materialSell, subsSell, sellTotal, margin, marginPct };
  }, [scopes, form.labor_markup_pct, form.material_markup_pct, form.overall_markup_pct]);

  // Auto-sync Contract $ ← Sell price whenever scopes or markup change, so the price
  // the client sees always includes the markup. User can still override the field manually
  // (which just sets a static value until the next markup/scope change).
  useEffect(() => {
    if (scopes.length === 0) return;
    setForm((f) => ({ ...f, contract_value: Number(pricing.sellTotal.toFixed(2)) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricing.sellTotal, scopes.length]);

  const applySellPriceAsContract = () => setForm((f) => ({ ...f, contract_value: Number(pricing.sellTotal.toFixed(2)) }));

  const create = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    if (!client.id) { toast.error("Select or register a client"); return; }
    setBusy(true);
    try {
      const p = await api.post("/projects", {
        ...form,
        contract_value: parseFloat(form.contract_value) || 0,
        labor_markup_pct: parseFloat(form.labor_markup_pct) || 0,
        material_markup_pct: parseFloat(form.material_markup_pct) || 0,
        overall_markup_pct: parseFloat(form.overall_markup_pct) || 0,
        client_id: client.id, client_name: client.name,
        property_id: property.id || null, property_name: property.name || "",
        phases, milestones: [], scopes,
      });
      toast.success(scopes.length > 0 ? `Project created with ${scopes.length} AI-generated scopes` : "Project created");
      onCreated(p);
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">New Project</h2>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Project name</label>
              <input data-testid="prj-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="26 Sky Lane — Full Renovation" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Client *</label>
              <ClientPicker value={client} onChange={setClient} />
              <div className="text-[11px] text-slate-400 mt-1">Select from the registry or register a new one inline. Email is used to send proposals.</div>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Property / Job site</label>
              <PropertyPicker value={property} clientId={client.id} onChange={setProperty} />
              <div className="text-[11px] text-slate-400 mt-1">Optional — leave blank if not applicable.</div>
            </div>
            <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Contract $</label>
              <input data-testid="prj-contract" type="number" step="0.01" value={form.contract_value} onChange={(e) => setForm({ ...form, contract_value: e.target.value })} className={`${inputCls} text-right tabular-nums`} /></div>
            <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Start</label>
              <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inputCls} /></div>
            <div className="col-span-2"><label className="block text-xs font-bold uppercase text-slate-500 mb-1">End</label>
              <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inputCls} /></div>

            {/* Project team — PM and Backoffice Analyst */}
            <div>
              <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Project Manager (PM)</label>
              <select
                data-testid="prj-pm-select"
                value={form.pm_id}
                onChange={(e) => {
                  const u = pms.find((x) => x.id === e.target.value);
                  setForm({ ...form, pm_id: u ? u.id : "", pm_name: u ? u.name : "" });
                }}
                className={inputCls}
              >
                <option value="">— none —</option>
                {pms.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Backoffice Analyst</label>
              <select
                data-testid="prj-analyst-select"
                value={form.analyst_id}
                onChange={(e) => {
                  const u = analysts.find((x) => x.id === e.target.value);
                  setForm({ ...form, analyst_id: u ? u.id : "", analyst_name: u ? u.name : "" });
                }}
                className={inputCls}
              >
                <option value="">— none —</option>
                {analysts.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>

            {/* Markup — applied when converting cost to selling price during estimate */}
            <div className="col-span-2 bg-purple-50/50 border border-purple-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold uppercase text-purple-800 tracking-wider">Estimate markup (%)</div>
                <div className="text-[10px] text-purple-600">Applied on top of your real cost when generating the proposal</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500">Labor markup</label>
                  <input type="number" step="1" min="0" max="200"
                    data-testid="prj-markup-labor"
                    value={form.labor_markup_pct}
                    onChange={(e) => setForm({ ...form, labor_markup_pct: parseFloat(e.target.value) || 0 })}
                    className={`${inputCls} text-right tabular-nums`} />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500">Material markup</label>
                  <input type="number" step="1" min="0" max="200"
                    data-testid="prj-markup-material"
                    value={form.material_markup_pct}
                    onChange={(e) => setForm({ ...form, material_markup_pct: parseFloat(e.target.value) || 0 })}
                    className={`${inputCls} text-right tabular-nums`} />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500">Overall markup</label>
                  <input type="number" step="1" min="0" max="200"
                    data-testid="prj-markup-overall"
                    value={form.overall_markup_pct}
                    onChange={(e) => setForm({ ...form, overall_markup_pct: parseFloat(e.target.value) || 0 })}
                    className={`${inputCls} text-right tabular-nums`} />
                </div>
              </div>
            </div>
          </div>

          {/* Pricing summary — Cost → Sell → Margin (lights up after AI generates scopes) */}
          {scopes.length > 0 && (
            <div className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white rounded-lg p-3" data-testid="prj-pricing-summary">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold uppercase text-emerald-800 tracking-wider inline-flex items-center gap-1.5">
                  <DollarSign className="w-3.5 h-3.5" /> Pricing summary
                </div>
                <button
                  type="button"
                  data-testid="prj-apply-sell-btn"
                  onClick={applySellPriceAsContract}
                  className="text-[10px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 rounded"
                >Use sell as Contract $</button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                <div className="bg-white rounded p-2 border border-slate-200">
                  <div className="text-[10px] font-bold uppercase text-slate-500">Cost total</div>
                  <div className="text-base font-bold tabular-nums text-slate-800">{money(pricing.cost)}</div>
                  <div className="text-[10px] text-slate-400">L {money(pricing.laborCost)} · M {money(pricing.materialCost)} · S {money(pricing.subsCost)}</div>
                </div>
                <div className="bg-white rounded p-2 border border-emerald-300">
                  <div className="text-[10px] font-bold uppercase text-emerald-700">Sell price (with markup)</div>
                  <div className="text-base font-bold tabular-nums text-emerald-700" data-testid="prj-sell-total">{money(pricing.sellTotal)}</div>
                  <div className="text-[10px] text-emerald-600">L {money(pricing.laborSell)} · M {money(pricing.materialSell)} · S {money(pricing.subsSell)}</div>
                </div>
                <div className={`bg-white rounded p-2 border ${pricing.margin >= 0 ? "border-emerald-300" : "border-red-300"}`}>
                  <div className="text-[10px] font-bold uppercase text-slate-500">Gross margin</div>
                  <div className={`text-base font-bold tabular-nums ${pricing.margin >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid="prj-margin">
                    {money(pricing.margin)}
                  </div>
                  <div className="text-[10px] text-slate-500">{pricing.marginPct.toFixed(1)}% of sell price</div>
                </div>
              </div>
              <div className="text-[10px] text-slate-500 mt-2 italic">
                Cost = what you spend on the job. Sell = Cost × (1 + markup) — this is what the client sees on the proposal.
                Click <b>Use sell as Contract $</b> to make the pricing official.
              </div>
            </div>
          )}

          <AIAssistSection onApply={applyAiScopes} currentScopes={scopes} onClearScopes={() => setScopes([])} />

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-bold uppercase text-slate-500">Phases</label>
              <button data-testid="prj-add-phase" onClick={addPhase} className="text-xs font-semibold text-blue-600 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add phase</button>
            </div>
            <div className="space-y-2">
              {phases.map((p, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center border border-slate-200 rounded-lg p-2 bg-slate-50/40">
                  <input placeholder="Phase name" value={p.name} onChange={(e) => patchPhase(i, { name: e.target.value })} className="col-span-4 rounded-md border border-slate-200 px-2 py-1.5 text-xs" />
                  <input type="number" placeholder="Labor $" value={p.budget_labor} onChange={(e) => patchPhase(i, { budget_labor: parseFloat(e.target.value) || 0 })} className="col-span-2 rounded-md border border-slate-200 px-2 py-1.5 text-xs text-right tabular-nums" />
                  <input type="number" placeholder="Material $" value={p.budget_material} onChange={(e) => patchPhase(i, { budget_material: parseFloat(e.target.value) || 0 })} className="col-span-2 rounded-md border border-slate-200 px-2 py-1.5 text-xs text-right tabular-nums" />
                  <input type="number" placeholder="Subs $" value={p.budget_subs} onChange={(e) => patchPhase(i, { budget_subs: parseFloat(e.target.value) || 0 })} className="col-span-2 rounded-md border border-slate-200 px-2 py-1.5 text-xs text-right tabular-nums" />
                  <div className="col-span-1 text-xs text-right tabular-nums font-semibold">{money((p.budget_labor || 0) + (p.budget_material || 0) + (p.budget_subs || 0))}</div>
                  <button onClick={() => removePhase(i)} disabled={phases.length === 1} className="col-span-1 p-1 text-slate-400 hover:text-red-600 disabled:opacity-30"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-5 py-3 border-t border-slate-200 sticky bottom-0 bg-white">
          <button onClick={onClose} disabled={busy} className="rounded-lg px-3 py-2 text-sm border border-slate-300 hover:bg-slate-50 disabled:opacity-40">Cancel</button>
          <button data-testid="prj-create" onClick={create} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800 disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Create
          </button>
        </div>
      </div>
    </div>
  );
}

/* ================= Status action bar (transitions) ================= */
function StatusActionBar({ project, user, onChange }) {
  const [busy, setBusy] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [approvalInfo, setApprovalInfo] = useState(null);

  const role = user.role;
  const status = project.status;
  const approvals = project.approvals || {};
  const needs = project.internal_approvals_needed || {};
  const lastRej = project.last_rejection;

  const call = async (path, body = null) => {
    setBusy(true);
    try {
      const res = body ? await api.post(path, body) : await api.post(path, {});
      toast.success("Done");
      if (res && res.approval_url) setApprovalInfo(res);
      onChange();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setBusy(false); }
  };

  const reject = async (path) => {
    const reason = window.prompt("Reason for rejection:");
    if (reason === null) return;
    await call(path, { reason });
  };

  const canAccept = ["admin", "manager", "analista", "pm"].includes(role);
  const canSubmit = ["admin", "manager", "analista", "estimator", "pm"].includes(role);
  const canPmApprove = ["admin", "manager", "pm"].includes(role) && needs.pm !== false;
  const canDirectorApprove = ["admin", "director"].includes(role);
  const canSendClient = ["admin", "manager", "pm"].includes(role);
  const canComplete = ["admin", "manager", "pm"].includes(role);
  const canHold = ["admin", "manager", "pm"].includes(role);
  const canCancel = ["admin", "manager"].includes(role);

  const buttons = [];

  if (status === "pending_acceptance" && canAccept) {
    buttons.push(
      <ActionBtn key="accept" data-testid="prj-tx-accept"
        onClick={() => call(`/projects/${project.id}/transitions/accept`)}
        icon={Check} label="Accept for estimation" color="bg-emerald-600 hover:bg-emerald-700" />
    );
  }

  if ((status === "draft" || status === "estimating") && canSubmit) {
    buttons.push(
      <ActionBtn key="submit" data-testid="prj-tx-submit"
        onClick={() => call(`/projects/${project.id}/transitions/submit-for-approval`)}
        icon={Send} label="Submit for internal approval" color="bg-slate-900 hover:bg-slate-800" />
    );
  }

  if (status === "pending_internal_approval") {
    if (needs.pm && canPmApprove && !approvals.pm) {
      buttons.push(
        <ActionBtn key="pm" data-testid="prj-tx-pm-approve"
          onClick={() => call(`/projects/${project.id}/transitions/pm-approve`)}
          icon={Check} label="Approve as PM" color="bg-blue-600 hover:bg-blue-700" />
      );
    }
    if (needs.director && canDirectorApprove && !approvals.director) {
      buttons.push(
        <ActionBtn key="dir" data-testid="prj-tx-director-approve"
          onClick={() => call(`/projects/${project.id}/transitions/director-approve`)}
          icon={Check} label="Approve as Director" color="bg-indigo-600 hover:bg-indigo-700" />
      );
    }
    if (canPmApprove || canDirectorApprove) {
      buttons.push(
        <ActionBtn key="rej" data-testid="prj-tx-reject"
          onClick={() => reject(`/projects/${project.id}/transitions/reject`)}
          icon={X} label="Reject" color="bg-rose-600 hover:bg-rose-700" outline />
      );
    }
  }

  if (status === "pending_client_approval" && canSendClient) {
    buttons.push(
      <ActionBtn key="send" data-testid="prj-tx-send-client"
        onClick={async () => {
          setBusy(true);
          try {
            const res = await api.post(`/projects/${project.id}/transitions/send-client-approval`, {});
            setApprovalInfo(res);
            setShowSendModal(true);
            toast.success(res.email_sent ? "Email sent to client" : "Approval link ready — copy it below");
            onChange();
          } catch (e) { toast.error(errMsg(e, "Failed")); }
          finally { setBusy(false); }
        }}
        icon={Send} label="Send client approval link" color="bg-indigo-600 hover:bg-indigo-700" />
    );
  }

  if (["approved", "in_progress"].includes(status) && canComplete) {
    buttons.push(
      <ActionBtn key="complete" data-testid="prj-tx-complete"
        onClick={() => call(`/projects/${project.id}/transitions/complete`)}
        icon={Check} label="Mark complete" color="bg-slate-900 hover:bg-slate-800" outline />
    );
  }

  if (["approved", "in_progress"].includes(status) && canHold) {
    buttons.push(
      <ActionBtn key="hold" data-testid="prj-tx-hold"
        onClick={() => reject(`/projects/${project.id}/transitions/hold`)}
        icon={AlertTriangle} label="Put on hold" color="bg-orange-500 hover:bg-orange-600" outline />
    );
  }

  if (status === "on_hold" && canHold) {
    buttons.push(
      <ActionBtn key="resume" data-testid="prj-tx-resume"
        onClick={() => call(`/projects/${project.id}/transitions/resume`)}
        icon={Play} label="Resume" color="bg-emerald-600 hover:bg-emerald-700" />
    );
  }

  if (canCancel && !["completed", "cancelled"].includes(status)) {
    buttons.push(
      <ActionBtn key="cancel" data-testid="prj-tx-cancel"
        onClick={() => reject(`/projects/${project.id}/transitions/cancel`)}
        icon={X} label="Cancel project" color="bg-rose-700 hover:bg-rose-800" outline />
    );
  }

  const approvalPills = [];
  if (approvals.analyst) approvalPills.push(<Pill key="a" color="bg-purple-100 text-purple-700 border-purple-200">Analyst ✓ {approvals.analyst.by_name}</Pill>);
  if (approvals.pm) approvalPills.push(<Pill key="p" color="bg-blue-100 text-blue-700 border-blue-200">PM ✓ {approvals.pm.by_name}</Pill>);
  if (approvals.director) approvalPills.push(<Pill key="d" color="bg-indigo-100 text-indigo-700 border-indigo-200">Director ✓ {approvals.director.by_name}</Pill>);
  if (approvals.client) approvalPills.push(<Pill key="c" color="bg-emerald-100 text-emerald-700 border-emerald-200">Client ✓ {approvals.client.by_name} <span className="opacity-60">({approvals.client.via})</span></Pill>);

  if (buttons.length === 0 && approvalPills.length === 0 && !lastRej) return null;

  return (
    <div className="px-5 py-2 bg-slate-50/70 border-b border-slate-200 flex flex-wrap items-center gap-2" data-testid="prj-status-bar">
      {buttons.length > 0 && (
        <div className={`flex flex-wrap gap-1.5 ${busy ? "opacity-60 pointer-events-none" : ""}`}>{buttons}</div>
      )}
      {approvalPills.length > 0 && (
        <div className="flex flex-wrap gap-1 ml-auto">{approvalPills}</div>
      )}
      {lastRej && (
        <div className="w-full text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 mt-1">
          <b>Rejected at {lastRej.stage}</b> by {lastRej.by_name}: <i>{lastRej.reason || "no reason given"}</i>
        </div>
      )}

      {showSendModal && approvalInfo && (
        <SendApprovalModal info={approvalInfo} onClose={() => setShowSendModal(false)} />
      )}
    </div>
  );
}

function ActionBtn({ onClick, icon: Icon, label, color, outline, ...rest }) {
  const base = "inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded transition-colors";
  const cls = outline
    ? `${base} border border-current text-current hover:bg-current/10 ${color.replace(/bg-\S+/, "").replace(/hover:bg-\S+/, "")} ${color.match(/bg-\S+/)?.[0]?.replace("bg-", "text-").replace(/-\d+$/, "-700") || ""}`
    : `${base} text-white ${color}`;
  return (
    <button onClick={onClick} className={outline ? `${base} border border-slate-300 hover:bg-slate-100 text-slate-700` : `${base} text-white ${color}`} {...rest}>
      <Icon className="w-3 h-3" /> {label}
    </button>
  );
}

function Pill({ color, children }) {
  return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${color}`}>{children}</span>;
}

function SendApprovalModal({ info, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(info.approval_url).then(() => {
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl p-5" onClick={(e) => e.stopPropagation()} data-testid="prj-send-approval-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">Client approval link</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        {info.email_sent ? (
          <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2 mb-3 inline-flex items-center gap-1">
            <Check className="w-3.5 h-3.5" /> Email sent to <b className="ml-1">{info.client_email}</b>
          </div>
        ) : (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
            {info.email_error
              ? <>Email service failed: <i>{info.email_error}</i>. Send the link manually below.</>
              : info.client_email
                ? <>Email not delivered. Copy the link below and send it manually to <b>{info.client_email}</b>.</>
                : <>No client email on file. Copy the link below and send it manually.</>}
          </div>
        )}
        <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Approval link</div>
        <div className="flex items-center gap-2">
          <input readOnly value={info.approval_url}
            className="flex-1 rounded border border-slate-300 px-2 py-1.5 text-xs bg-slate-50 tabular-nums"
            data-testid="prj-approval-url"
          />
          <button onClick={copy} data-testid="prj-approval-copy"
            className="text-xs font-bold px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1">
            <Copy className="w-3.5 h-3.5" /> {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <div className="text-[10px] text-slate-400 mt-2">The client will see a simplified proposal view and can Approve or Reject without logging in.</div>
      </div>
    </div>
  );
}

/* ================= Detail modal ================= */
// KPI bar rendered below the header/totals — helps PMs spot stalled projects,
// pending change orders and upcoming/overdue phase end dates without needing
// to open the WBS.
function ProjectKPIBar({ project }) {
  const scopes = project.scopes || [];
  const changeOrders = project.change_orders || [];
  const now = Date.now();
  const dayMs = 86400000;

  const ageAnchor = project.status_updated_at || project.updated_at || project.created_at;
  const ageDays = ageAnchor ? Math.max(0, Math.floor((now - new Date(ageAnchor).getTime()) / dayMs)) : null;

  const upcomingPhases = scopes
    .filter((s) => s.end_date && !["completed", "done", "cancelled"].includes((s.status || "").toLowerCase()))
    .map((s) => ({ ...s, ts: new Date(s.end_date).getTime() }))
    .filter((s) => !isNaN(s.ts))
    .sort((a, b) => a.ts - b.ts);
  const nextPhase = upcomingPhases[0];
  const nextPhaseDays = nextPhase ? Math.ceil((nextPhase.ts - now) / dayMs) : null;

  const totalPhases = scopes.length;
  const inProgress = scopes.filter((s) => (s.status || "").toLowerCase() === "in_progress").length;
  const completed = scopes.filter((s) => ["completed", "done"].includes((s.status || "").toLowerCase())).length;
  const pendingCO = changeOrders.filter((co) => ["pending", "pending_approval", "draft"].includes((co.status || "").toLowerCase())).length;

  const statusLabel = ({
    pending_acceptance: "Awaiting client",
    estimating: "In estimating",
    pending_internal: "Pending internal review",
    pending_client: "Awaiting client sign-off",
    ready_to_start: "Ready to start",
    in_progress: "In progress",
    on_hold: "On hold",
    completed: "Completed",
    closed: "Closed",
    cancelled: "Cancelled",
    draft: "Draft",
  })[project.status] || project.status;

  const ageWarn = ageDays !== null && ageDays >= 14 && ["pending_client", "pending_internal", "estimating"].includes(project.status);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 px-3 md:px-5 py-2.5 border-b border-slate-200 bg-white text-xs" data-testid="prj-kpi-bar">
      <div className={`rounded-md p-2 border ${ageWarn ? "border-amber-200 bg-amber-50" : "border-slate-100 bg-slate-50/50"}`}>
        <div className="text-[9px] uppercase font-bold text-slate-400 tracking-wide">{statusLabel}</div>
        <div className={`font-semibold tabular-nums ${ageWarn ? "text-amber-700" : "text-slate-700"}`}>
          {ageDays === null ? "—" : `${ageDays}d ago`}
        </div>
        {ageWarn && <div className="text-[9px] text-amber-600 mt-0.5">Stalled — needs a nudge</div>}
      </div>
      <div className="rounded-md p-2 border border-slate-100 bg-slate-50/50">
        <div className="text-[9px] uppercase font-bold text-slate-400 tracking-wide">Phases</div>
        <div className="font-semibold tabular-nums text-slate-700">{completed} / {totalPhases}</div>
        <div className="text-[9px] text-slate-500 mt-0.5">{inProgress} in progress</div>
      </div>
      <div className={`rounded-md p-2 border ${nextPhaseDays !== null && nextPhaseDays < 0 ? "border-rose-200 bg-rose-50" : nextPhaseDays !== null && nextPhaseDays < 7 ? "border-amber-200 bg-amber-50" : "border-slate-100 bg-slate-50/50"}`}>
        <div className="text-[9px] uppercase font-bold text-slate-400 tracking-wide">Next milestone</div>
        {nextPhase ? (
          <>
            <div className="font-semibold text-slate-800 truncate" title={nextPhase.name}>{nextPhase.name}</div>
            <div className={`text-[9px] tabular-nums mt-0.5 ${nextPhaseDays < 0 ? "text-rose-600 font-bold" : nextPhaseDays < 7 ? "text-amber-600 font-bold" : "text-slate-500"}`}>
              {nextPhaseDays < 0 ? `Overdue ${Math.abs(nextPhaseDays)}d` : nextPhaseDays === 0 ? "Due today" : `In ${nextPhaseDays}d`}
              {" · "}{nextPhase.end_date}
            </div>
          </>
        ) : (
          <div className="text-slate-400 font-semibold">—</div>
        )}
      </div>
      <div className={`rounded-md p-2 border ${pendingCO > 0 ? "border-blue-200 bg-blue-50" : "border-slate-100 bg-slate-50/50"}`}>
        <div className="text-[9px] uppercase font-bold text-slate-400 tracking-wide">Change orders</div>
        <div className={`font-semibold tabular-nums ${pendingCO > 0 ? "text-blue-700" : "text-slate-700"}`}>{pendingCO} pending</div>
        <div className="text-[9px] text-slate-500 mt-0.5">{changeOrders.length} total</div>
      </div>
    </div>
  );
}

function ProjectDetailModal({ project, user, onClose, onChange }) {
  const [tab, setTab] = useState("wbs");
  const [payTarget, setPayTarget] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payNumber, setPayNumber] = useState("");
  const [payPhoto, setPayPhoto] = useState(null);
  const t2 = project.totals || {};
  const ui = STATUS_UI[project.status] || STATUS_UI.draft;
  const canManage = ["admin", "manager", "pm", "estimator"].includes(user.role);
  const canApproveCO = ["admin", "manager", "director"].includes(user.role);
  const canFinance = ["admin", "manager", "financeiro"].includes(user.role);

  const call = async (fn) => {
    try { await fn(); onChange(); }
    catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  const uploadPhoto = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPayPhoto(reader.result);
    reader.readAsDataURL(file);
  };
  const submitPayment = async () => {
    if (!payAmount || !payTarget) return;
    await call(() => api.post(`/projects/${project.id}/scopes/${payTarget.id}/sub-invoice`, {
      amount: parseFloat(payAmount) || 0,
      number: payNumber || `INV-${Date.now()}`,
      photo_data_url: payPhoto || null,
    }));
    setPayTarget(null); setPayAmount(""); setPayNumber(""); setPayPhoto(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-0 md:p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-none md:rounded-xl shadow-xl w-full max-w-6xl h-full md:h-[95vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-3 md:px-5 py-3 border-b border-slate-200">
          <div className="min-w-0">
            <div className="text-xs text-slate-400 font-medium">{project.number}</div>
            <div className="text-lg font-semibold text-slate-900 truncate">{project.name}</div>
            <div className="text-xs text-slate-500 mt-0.5">{project.client_name} · {project.property_name}</div>
            {(project.pm_name || project.analyst_name) && (
              <div className="text-[10px] text-slate-500 mt-1 flex flex-wrap gap-2" data-testid="prj-team">
                {project.pm_name && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100">
                    <UserCircle className="w-3 h-3" /> PM: <b>{project.pm_name}</b>
                  </span>
                )}
                {project.analyst_name && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-100">
                    <UserCircle className="w-3 h-3" /> Analyst: <b>{project.analyst_name}</b>
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-full border ${ui.color}`}>{ui.label}</span>
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Contextual action bar — status transitions */}
        <StatusActionBar project={project} user={user} onChange={onChange} />

        {/* Totals bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 px-3 md:px-5 py-3 bg-slate-50 border-b border-slate-200 text-xs">
          <div><div className="text-[10px] uppercase font-bold text-slate-400">Contract</div><div className="font-semibold tabular-nums">{money(t2.contract_effective)}</div>{t2.change_orders_price > 0 && <div className="text-[10px] text-blue-600">+{money(t2.change_orders_price)} COs</div>}</div>
          <div><div className="text-[10px] uppercase font-bold text-slate-400">Budget</div><div className="font-semibold tabular-nums">{money(t2.budget_total)}</div></div>
          <div><div className="text-[10px] uppercase font-bold text-slate-400">Invoiced / Paid</div><div className="font-semibold tabular-nums text-emerald-700">{money(t2.invoiced)} / {money(t2.paid)}</div></div>
          <div><div className="text-[10px] uppercase font-bold text-slate-400">Progress</div><div className="font-semibold tabular-nums">{t2.progress_pct || 0}%</div><div className="mt-1 h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${t2.progress_pct || 0}%` }} /></div></div>
        </div>

        {/* KPI bar — extra status signals to catch stalled projects at a glance */}
        <ProjectKPIBar project={project} />

        <div className="flex flex-wrap gap-x-1 gap-y-0 px-2 md:px-5 border-b border-slate-200">
          {[["wbs", Layers, "WBS"], ["overview", ClipboardList, "Overview"], ["daily_reports", FileText, "Daily reports"], ["change_orders", TrendingUp, "Change orders"], ["invoices", DollarSign, "Invoices"], ["materials", Package, "Materials"], ["allocation", Clock, "Time allocation"], ["schedule", Clock, "Schedule"], ["financials", DollarSign, "Financials"], ["documents", FileText, "Documents"], ["proposal", FileText, "Proposal"]].map(([k, Icon, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`prj-tab-${k}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 -mb-px whitespace-nowrap ${
                tab === k ? "text-slate-900 border-slate-900" : "text-slate-500 border-transparent hover:text-slate-700"
              }`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-3 md:p-5 space-y-4">
          {tab === "wbs" && <WBSTab project={project} canManage={canManage} canFinance={canFinance} onCall={call} onPay={setPayTarget} />}
          {tab === "overview" && <OverviewTab project={project} user={user} onChange={onChange} />}
          {tab === "daily_reports" && <DailyReports projectId={project.id} />}
          {tab === "change_orders" && <ChangeOrdersTab project={project} canManage={canManage} canApprove={canApproveCO} onCall={call} />}
          {tab === "invoices" && <InvoicesTab project={project} canManage={canManage} canFinance={canFinance} />}
          {tab === "materials" && <MaterialRequestsTab project={project} user={user} />}
          {tab === "allocation" && <TimeAllocationTab project={project} />}
          {tab === "schedule" && <ScheduleGanttTab project={project} />}
          {tab === "financials" && <FinancialsTab project={project} />}
          {tab === "documents" && <DocumentsTab project={project} canManage={canManage} onCall={call} />}
          {tab === "proposal" && <ProposalTab project={project} canManage={canManage} onCall={call} onChange={onChange} />}
        </div>
      </div>

      {payTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50" onClick={() => setPayTarget(null)}>
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h3 className="text-sm font-semibold">Pay sub — {payTarget.name}</h3>
              <button onClick={() => setPayTarget(null)} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div><label className="text-xs font-bold uppercase text-slate-500">Amount $</label>
                <input type="number" step="0.01" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className={inputCls} data-testid="prj-payform-amount" /></div>
              <div><label className="text-xs font-bold uppercase text-slate-500">Invoice number</label>
                <input value={payNumber} onChange={(e) => setPayNumber(e.target.value)} className={inputCls} placeholder="INV-2607-001" /></div>
              <div>
                <label className="text-xs font-bold uppercase text-slate-500">Invoice photo (optional)</label>
                {payPhoto ? (
                  <div className="relative"><img src={payPhoto} alt="" className="w-full max-h-40 object-contain bg-slate-50 rounded-lg border border-slate-200" />
                    <button onClick={() => setPayPhoto(null)} className="absolute top-2 right-2 bg-white border border-slate-200 rounded px-2 py-0.5 text-xs">Remove</button></div>
                ) : (
                  <label className="flex items-center justify-center h-24 border-2 border-dashed border-slate-300 rounded-lg text-slate-400 hover:border-slate-500 cursor-pointer text-xs">
                    Tap to add photo
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
                  </label>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
              <button onClick={() => setPayTarget(null)} className="text-xs px-3 py-1.5 rounded border border-slate-200">Cancel</button>
              <button data-testid="prj-payform-submit" onClick={submitPayment} className="text-xs px-3 py-1.5 rounded bg-emerald-600 text-white font-semibold inline-flex items-center gap-1"><Check className="w-3 h-3" /> Record payment</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OverviewTab({ project, user, onChange }) {
  const activity = project.activity || [];
  const [busy, setBusy] = useState(false);
  const [savingTeam, setSavingTeam] = useState(false);
  const myOpen = (project.time_entries || []).find((e) => e.tech_id === user?.id && !e.clock_out);
  const pms = useUsersByRole("pm");
  const analysts = useUsersByRole("analista");
  const canManage = ["admin", "manager", "pm", "estimator"].includes(user?.role);

  const assignTeam = async (patch) => {
    setSavingTeam(true);
    try {
      await api.patch(`/projects/${project.id}`, patch);
      toast.success("Team updated");
      onChange && onChange();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setSavingTeam(false); }
  };

  const clock = async (action) => {
    setBusy(true);
    try {
      const pos = await new Promise((res) => {
        if (!navigator.geolocation) return res({});
        navigator.geolocation.getCurrentPosition(
          (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
          () => res({}), { timeout: 5000 });
      });
      await api.post(`/projects/${project.id}/${action}`, pos);
      toast.success(action === "clock-in" ? "Clock-in registered" : "Clock-out registered");
      onChange && onChange();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setBusy(false); }
  };

  return (
    <>
      {/* Time clock quick actions */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="prj-overview-timeclock">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/10 rounded-lg">
            <Clock className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-300 font-bold">Project time clock</div>
            <div className="text-xs text-slate-200">
              {myOpen ? `Open since ${myOpen.clock_in?.slice(11, 16)}` : "Not started"}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {!myOpen ? (
            <button
              data-testid="prj-clock-in"
              onClick={() => clock("clock-in")} disabled={busy}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold px-3 py-2 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} Check-in
            </button>
          ) : (
            <button
              data-testid="prj-clock-out"
              onClick={() => clock("clock-out")} disabled={busy}
              className="inline-flex items-center gap-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-xs font-bold px-3 py-2 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Square className="w-3 h-3" />} Check-out
            </button>
          )}
        </div>
      </div>

      {(project.time_entries || []).length > 0 && (
        <div>
          <div className="text-xs font-bold uppercase text-slate-400 mb-1">Time log</div>
          <ul className="text-xs text-slate-600 border border-slate-200 rounded-lg divide-y divide-slate-100">
            {project.time_entries.slice(-5).reverse().map((e) => (
              <li key={e.id} className="flex items-center justify-between px-2.5 py-1.5">
                <span className="font-medium">{e.tech_name}</span>
                <span className="tabular-nums text-slate-500">
                  {e.clock_in?.slice(11, 16)} → {e.clock_out ? e.clock_out.slice(11, 16) : <b className="text-emerald-600">open…</b>}
                  {e.hours ? <span className="ml-2 text-slate-700 font-semibold">{e.hours.toFixed(2)}h</span> : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid="prj-overview-team">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-bold uppercase text-slate-500 inline-flex items-center gap-1.5">
            <UserCircle className="w-3.5 h-3.5" /> Project team
          </div>
          {savingTeam && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">PM (Project Manager)</label>
            <select
              data-testid="prj-overview-pm"
              disabled={!canManage || savingTeam}
              value={project.pm_id || ""}
              onChange={(e) => {
                const u = pms.find((x) => x.id === e.target.value);
                assignTeam({ pm_id: u ? u.id : "", pm_name: u ? u.name : "" });
              }}
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs focus:ring-2 focus:ring-slate-900 outline-none disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <option value="">— unassigned —</option>
              {pms.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Analyst</label>
            <select
              data-testid="prj-overview-analyst"
              disabled={!canManage || savingTeam}
              value={project.analyst_id || ""}
              onChange={(e) => {
                const u = analysts.find((x) => x.id === e.target.value);
                assignTeam({ analyst_id: u ? u.id : "", analyst_name: u ? u.name : "" });
              }}
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs focus:ring-2 focus:ring-slate-900 outline-none disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <option value="">— unassigned —</option>
              {analysts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div>
        <div className="text-xs font-bold uppercase text-slate-400 mb-1">Notes</div>
        <p className="text-sm text-slate-700 whitespace-pre-wrap">{project.notes || <span className="text-slate-400 italic">No notes</span>}</p>
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div><div className="text-slate-400 font-bold uppercase">Start</div><div>{project.start_date || "—"}</div></div>
        <div><div className="text-slate-400 font-bold uppercase">End</div><div>{project.end_date || "—"}</div></div>
      </div>
      <div>
        <div className="text-xs font-bold uppercase text-slate-400 mb-2">Activity</div>
        <ul className="text-xs text-slate-500 space-y-1 max-h-56 overflow-y-auto pr-1">
          {activity.map((a, i) => (
            <li key={i} className="flex items-start gap-2">
              <ChevronRight className="w-3 h-3 mt-0.5 shrink-0 text-slate-300" />
              <span><span className="font-semibold text-slate-700">{a.user_name}</span> — {a.action} <span className="text-slate-400">• {a.at?.slice(0, 16).replace("T", " ")}</span></span>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

function PhasesTab({ project, canManage, onCall }) {
  return (
    <div className="space-y-2" data-testid="prj-phases-list">
      {(project.phases || []).map((p) => {
        const total = (p.budget_labor || 0) + (p.budget_material || 0) + (p.budget_subs || 0);
        const st = PHASE_STATUS_UI[p.status] || PHASE_STATUS_UI.not_started;
        return (
          <div key={p.id} className="border border-slate-200 rounded-lg p-3 bg-slate-50/40" data-testid={`prj-phase-${p.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center shrink-0">{p.order}</span>
                <span className="font-medium text-slate-900 truncate">{p.name}</span>
                <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
              </div>
              <div className="text-sm font-semibold tabular-nums">{money(total)}</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-[11px]">
              <div><div className="text-slate-400 font-bold uppercase">Labor</div><div className="tabular-nums">{money(p.budget_labor)}</div></div>
              <div><div className="text-slate-400 font-bold uppercase">Material</div><div className="tabular-nums">{money(p.budget_material)}</div></div>
              <div><div className="text-slate-400 font-bold uppercase">Subs</div><div className="tabular-nums">{money(p.budget_subs)}</div></div>
              <div><div className="text-slate-400 font-bold uppercase">Progress</div><div>{p.progress_pct || 0}%</div></div>
            </div>
            {canManage && (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <select
                  data-testid={`prj-phase-status-${p.id}`}
                  value={p.status}
                  onChange={(e) => onCall(() => api.patch(`/projects/${project.id}/phases/${p.id}`, { ...p, status: e.target.value }))}
                  className="text-[11px] rounded border border-slate-200 px-1.5 py-0.5"
                >
                  {Object.entries(PHASE_STATUS_UI).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <input type="number" min="0" max="100" value={p.progress_pct || 0}
                  onChange={(e) => onCall(() => api.patch(`/projects/${project.id}/phases/${p.id}`, { ...p, progress_pct: parseFloat(e.target.value) || 0 }))}
                  className="w-16 text-[11px] rounded border border-slate-200 px-1.5 py-0.5 text-right"
                />
                <span className="text-[10px] text-slate-400">% complete</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ChangeOrdersTab({ project, canManage, canApprove, onCall }) {
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({ description: "", reason: "", cost_delta: 0, price_delta: 0 });

  const submit = async () => {
    if (!f.description) { toast.error("Description required"); return; }
    await onCall(() => api.post(`/projects/${project.id}/change-orders`, {
      ...f,
      cost_delta: parseFloat(f.cost_delta) || 0,
      price_delta: parseFloat(f.price_delta) || 0,
    }));
    setF({ description: "", reason: "", cost_delta: 0, price_delta: 0 });
    setShowForm(false);
  };

  return (
    <div className="space-y-2">
      {canManage && !showForm && (
        <button data-testid="prj-add-co" onClick={() => setShowForm(true)} className="text-xs font-semibold text-blue-600 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> New change order</button>
      )}
      {showForm && (
        <div className="border border-blue-200 bg-blue-50/40 rounded-lg p-3 space-y-2">
          <input placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inputCls} />
          <input placeholder="Reason (optional)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className={inputCls} />
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[10px] font-bold uppercase text-slate-500">Our cost $</label><input type="number" step="0.01" value={f.cost_delta} onChange={(e) => setF({ ...f, cost_delta: e.target.value })} className={inputCls} /></div>
            <div><label className="text-[10px] font-bold uppercase text-slate-500">Client price $</label><input type="number" step="0.01" value={f.price_delta} onChange={(e) => setF({ ...f, price_delta: e.target.value })} className={inputCls} /></div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="text-xs px-2 py-1 rounded border border-slate-200">Cancel</button>
            <button data-testid="prj-co-submit" onClick={submit} className="text-xs px-3 py-1 rounded bg-slate-900 text-white font-semibold">Submit</button>
          </div>
        </div>
      )}
      {(project.change_orders || []).length === 0 && !showForm && (
        <div className="text-center text-xs text-slate-400 py-6">No change orders yet.</div>
      )}
      {(project.change_orders || []).map((c) => (
        <div key={c.id} className="border border-slate-200 rounded-lg p-3" data-testid={`prj-co-${c.id}`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] text-slate-400 font-bold">{c.number}</div>
              <div className="font-medium text-slate-900 text-sm">{c.description}</div>
              {c.reason && <div className="text-[11px] text-slate-500 mt-0.5">{c.reason}</div>}
            </div>
            <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-full border ${
              c.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : c.status === "rejected" ? "bg-red-50 text-red-700 border-red-200"
              : "bg-amber-50 text-amber-700 border-amber-200"
            }`}>{c.status}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
            <div><div className="text-[10px] uppercase text-slate-400 font-bold">Our cost</div><div className="tabular-nums">{money(c.cost_delta)}</div></div>
            <div><div className="text-[10px] uppercase text-slate-400 font-bold">Client price</div><div className="tabular-nums text-emerald-700 font-semibold">{money(c.price_delta)}</div></div>
          </div>
          {canApprove && c.status === "pending" && (
            <div className="flex gap-2 mt-2">
              <button data-testid={`prj-co-approve-${c.id}`} onClick={() => onCall(() => api.post(`/projects/${project.id}/change-orders/${c.id}/decide?decision=approved`))} className="text-xs px-3 py-1 rounded bg-emerald-600 text-white font-semibold">Approve</button>
              <button onClick={() => onCall(() => api.post(`/projects/${project.id}/change-orders/${c.id}/decide?decision=rejected`))} className="text-xs px-3 py-1 rounded bg-red-500 text-white font-semibold">Reject</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* Scope preview inside Proposal tab — same content that appears in the PDF */
function ProposalScopePreview({ project }) {
  const scopes = project.scopes || [];
  const phases = project.phases || [];
  const milestones = project.milestones || [];
  const scopeCost = (sc) => {
    const steps = (sc.steps || []).reduce((a, st) => a + (st.quantity || 0) * (st.unit_price || 0), 0);
    const sub = sc.subcontract ? (sc.subcontract.billed_to_client || 0) : 0;
    return steps + sub;
  };
  if (scopes.length === 0 && milestones.length === 0) {
    return (
      <div className="border border-dashed border-slate-300 rounded-lg p-4 text-center text-xs text-slate-400">
        No scopes or milestones yet — this proposal PDF will look empty. Add scopes in the WBS tab.
      </div>
    );
  }

  const grouped = phases.length > 0
    ? phases.map((ph) => ({ phase: ph, scopes: scopes.filter((s) => s.phase_id === ph.id) }))
    : [{ phase: null, scopes }];
  const unassigned = scopes.filter((s) => !phases.some((p) => p.id === s.phase_id));
  if (phases.length > 0 && unassigned.length > 0) grouped.push({ phase: null, scopes: unassigned });

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="prj-proposal-scope-preview">
      <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase text-slate-500 tracking-widest">Scope preview — will appear on the PDF</div>
        <div className="text-[10px] text-slate-400">{scopes.length} scope{scopes.length !== 1 ? "s" : ""}</div>
      </div>
      <div className="p-3 space-y-3">
        {grouped.map(({ phase, scopes: phScopes }, i) => phScopes.length > 0 && (
          <div key={phase?.id || `x${i}`}>
            {phase && (
              <div className="text-xs font-bold uppercase tracking-wide text-slate-700 mb-1.5 flex items-center gap-2">
                <Layers className="w-3.5 h-3.5" /> Phase {phase.order || i + 1} — {phase.name}
              </div>
            )}
            <div className="space-y-1.5">
              {phScopes.map((sc) => (
                <div key={sc.id} className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                  <div className="flex items-center justify-between bg-slate-100 px-3 py-1.5">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-xs font-semibold text-slate-900 truncate">{sc.name}</span>
                      {sc.trade && <span className="text-[9px] font-bold uppercase bg-white text-slate-600 px-1 rounded shrink-0 border border-slate-200">{sc.trade}</span>}
                      {sc.subcontract?.subcontractor_name && (
                        <span className="text-[9px] italic text-slate-500 shrink-0">via {sc.subcontract.subcontractor_name}</span>
                      )}
                    </div>
                    <span className="text-xs font-bold tabular-nums text-slate-900">{money(scopeCost(sc))}</span>
                  </div>
                  {(sc.steps || []).length > 0 && (
                    <div className="px-3 py-2 space-y-0.5 text-[11px]">
                      {sc.steps.map((st) => (
                        <div key={st.id} className="flex justify-between text-slate-600">
                          <span className="truncate">• {st.description}</span>
                          <span className="tabular-nums shrink-0 ml-2 text-slate-400">{st.quantity} {st.unit}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {milestones.length > 0 && (
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-slate-700 mb-1.5 flex items-center gap-2">
              <DollarSign className="w-3.5 h-3.5" /> Payment Schedule
            </div>
            <table className="w-full text-[11px]">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide text-[10px]">
                <tr>
                  <th className="text-left px-2 py-1 font-bold">Milestone</th>
                  <th className="text-right px-2 py-1 font-bold">%</th>
                  <th className="text-right px-2 py-1 font-bold">Amount</th>
                  <th className="text-right px-2 py-1 font-bold">Due</th>
                </tr>
              </thead>
              <tbody>
                {milestones.map((m) => (
                  <tr key={m.id} className="border-t border-slate-100">
                    <td className="px-2 py-1">{m.name}</td>
                    <td className="text-right px-2 py-1 tabular-nums">{m.percent_of_contract || 0}%</td>
                    <td className="text-right px-2 py-1 tabular-nums font-semibold">{money(m.amount)}</td>
                    <td className="text-right px-2 py-1 text-slate-500">{m.due_date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ProposalTab({ project, canManage, onCall, onChange }) {
  const prop = project.proposal || {};
  const [validity, setValidity] = useState(prop.validity_days || 30);
  const [terms, setTerms] = useState(prop.terms || "");
  const [emailTo, setEmailTo] = useState("");
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);

  const totals = project.totals || {};
  const contract = totals.contract_effective || project.contract_value || 0;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const publicUrl = prop.public_token
    ? `${backendUrl}/api/proposals/public/${project.id}?token=${prop.public_token}`
    : "";

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.patch(`/projects/${project.id}/proposal`, {
        validity_days: parseInt(validity, 10) || 30,
        terms,
      });
      toast.success("Proposal saved");
      onChange();
    } catch (e) {
      toast.error(errMsg(e, "Failed to save"));
    } finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    try {
      const blob = await api.getBlob(`/projects/${project.id}/proposal/pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `proposal-${project.number || "project"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e, "Failed to download"));
    }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const q = emailTo ? `?to=${encodeURIComponent(emailTo)}` : "";
      const res = await api.post(`/projects/${project.id}/proposal/send${q}`, {});
      toast.success(`Proposal sent to ${res.sent_to}`);
      onChange();
    } catch (e) {
      toast.error(errMsg(e, "Failed to send"));
    } finally { setSending(false); }
  };

  const copyLink = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    toast.success("Link copied");
  };

  return (
    <div className="space-y-5" data-testid="prj-proposal-tab">
      {/* Summary card */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white rounded-xl p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-300 font-bold">Proposal</div>
            <div className="text-lg font-semibold mt-0.5">{project.name}</div>
            <div className="text-xs text-slate-300">No. {project.number} · {project.client_name || "—"}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase text-slate-300">Contract</div>
            <div className="text-2xl font-bold tabular-nums">{money(contract)}</div>
          </div>
        </div>
        {prop.accepted_at ? (
          <div className="mt-3 bg-emerald-500/20 border border-emerald-400 rounded-lg px-3 py-2 text-xs">
            <div className="flex items-center gap-2 font-semibold text-emerald-100">
              <Check className="w-4 h-4" /> Accepted electronically
            </div>
            <div className="text-emerald-100/90 mt-0.5">
              {prop.signature_name} · {prop.accepted_at?.slice(0, 16).replace("T", " ")} · IP {prop.signature_ip}
            </div>
          </div>
        ) : prop.sent_at ? (
          <div className="mt-3 bg-blue-500/20 border border-blue-400 rounded-lg px-3 py-2 text-xs text-blue-50">
            Sent to <b>{prop.sent_to}</b> · {prop.sent_at?.slice(0, 16).replace("T", " ")} · Awaiting signature
          </div>
        ) : (
          <div className="mt-3 bg-amber-500/20 border border-amber-400 rounded-lg px-3 py-2 text-xs text-amber-50">
            Draft — not yet sent to client
          </div>
        )}
      </div>

      {/* Scope preview (mirrors PDF) */}
      <ProposalScopePreview project={project} />

      {/* Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <button
          data-testid="prj-proposal-download"
          onClick={downloadPdf}
          className="inline-flex items-center justify-center gap-2 text-sm font-semibold px-3 py-2.5 rounded-lg border border-slate-300 hover:bg-slate-50"
        >
          <Download className="w-4 h-4" /> Download PDF
        </button>
        {canManage && (
          <button
            data-testid="prj-proposal-send"
            onClick={sendEmail}
            disabled={sending}
            className="inline-flex items-center justify-center gap-2 text-sm font-semibold px-3 py-2.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            {sending ? "Sending..." : "Send to client"}
          </button>
        )}
        {publicUrl && (
          <button
            data-testid="prj-proposal-copy"
            onClick={copyLink}
            className="inline-flex items-center justify-center gap-2 text-sm font-semibold px-3 py-2.5 rounded-lg border border-slate-300 hover:bg-slate-50"
          >
            <Copy className="w-4 h-4" /> Copy public link
          </button>
        )}
      </div>

      {canManage && (
        <div className="border border-slate-200 rounded-xl p-4 space-y-3 bg-white">
          <div>
            <label className="text-[10px] font-bold uppercase text-slate-500">Send to (overrides client email)</label>
            <input
              data-testid="prj-proposal-to"
              type="email"
              placeholder="client@example.com — leave blank to use client's email on file"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              className={inputCls}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-1">
              <label className="text-[10px] font-bold uppercase text-slate-500">Validity (days)</label>
              <input
                data-testid="prj-proposal-validity"
                type="number" min="1" max="365"
                value={validity}
                onChange={(e) => setValidity(e.target.value)}
                className={inputCls}
              />
            </div>
            <div className="md:col-span-2 flex items-end">
              <button
                data-testid="prj-proposal-save"
                onClick={saveDraft}
                disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold px-3 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Save draft
              </button>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase text-slate-500">Terms &amp; Conditions</label>
            <textarea
              data-testid="prj-proposal-terms"
              rows={8}
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              placeholder="Warranty, payment terms, exclusions, change-order policy..."
              className={`${inputCls} font-mono text-xs leading-relaxed`}
            />
            <div className="text-[11px] text-slate-400 mt-1">
              Leave blank to use the JMO standard terms embedded in the PDF.
            </div>
          </div>
        </div>
      )}

      {publicUrl && (
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 text-xs">
          <div className="font-bold text-blue-900 mb-1">Public signature link</div>
          <div className="text-blue-800 break-all">{publicUrl}</div>
          <div className="text-blue-700/80 mt-1">Client can accept without logging in. Signature = typed name + checkbox.</div>
        </div>
      )}
    </div>
  );
}

function MilestonesTab({ project, canManage, canFinance, onCall }) {
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({ name: "", percent_of_contract: 0, amount: 0, due_date: "" });
  const contract = useMemo(() => project.totals?.contract_effective || project.contract_value || 0, [project]);

  const submit = async () => {
    if (!f.name) { toast.error("Name required"); return; }
    await onCall(() => api.post(`/projects/${project.id}/milestones`, {
      name: f.name,
      percent_of_contract: parseFloat(f.percent_of_contract) || 0,
      amount: parseFloat(f.amount) || (contract * (parseFloat(f.percent_of_contract) || 0) / 100),
      due_date: f.due_date || null,
    }));
    setF({ name: "", percent_of_contract: 0, amount: 0, due_date: "" });
    setShowForm(false);
  };

  return (
    <div className="space-y-2">
      {canManage && !showForm && (
        <button data-testid="prj-add-ms" onClick={() => setShowForm(true)} className="text-xs font-semibold text-blue-600 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> New milestone</button>
      )}
      {showForm && (
        <div className="border border-blue-200 bg-blue-50/40 rounded-lg p-3 space-y-2">
          <input placeholder="Milestone name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input type="number" placeholder="%" value={f.percent_of_contract} onChange={(e) => setF({ ...f, percent_of_contract: e.target.value, amount: (contract * (parseFloat(e.target.value) || 0) / 100).toFixed(2) })} className={inputCls} />
            <input type="number" placeholder="Amount $" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={inputCls} />
            <input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className={inputCls} />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="text-xs px-2 py-1 rounded border border-slate-200">Cancel</button>
            <button data-testid="prj-ms-submit" onClick={submit} className="text-xs px-3 py-1 rounded bg-slate-900 text-white font-semibold">Save</button>
          </div>
        </div>
      )}
      {(project.milestones || []).map((m) => (
        <div key={m.id} className="border border-slate-200 rounded-lg p-3" data-testid={`prj-ms-${m.id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="font-medium text-slate-900 truncate">{m.name}</div>
              <div className="text-[11px] text-slate-500">{m.percent_of_contract}% • due {m.due_date || "—"}</div>
            </div>
            <div className="text-right">
              <div className="text-sm font-semibold tabular-nums">{money(m.amount)}</div>
              <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-full ${
                m.status === "paid" ? "bg-emerald-50 text-emerald-700"
                : m.status === "invoiced" ? "bg-blue-50 text-blue-700"
                : "bg-slate-100 text-slate-500"
              }`}>{m.status}</span>
            </div>
          </div>
          {canFinance && (
            <div className="flex gap-2 mt-2">
              {m.status === "pending" && (
                <button data-testid={`prj-ms-invoice-${m.id}`} onClick={() => onCall(() => api.post(`/projects/${project.id}/milestones/${m.id}/invoice`))} className="text-xs px-3 py-1 rounded bg-blue-600 text-white font-semibold inline-flex items-center gap-1"><Send className="w-3 h-3" /> Invoice</button>
              )}
              {m.status === "invoiced" && (
                <button data-testid={`prj-ms-paid-${m.id}`} onClick={() => onCall(() => api.post(`/projects/${project.id}/milestones/${m.id}/mark-paid`))} className="text-xs px-3 py-1 rounded bg-emerald-600 text-white font-semibold inline-flex items-center gap-1"><Check className="w-3 h-3" /> Mark paid</button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ================= Schedule Wizard — auto-compute dates from man-hours ================= */
function ScheduleWizard({ project, phases, phaseScope, onCall }) {
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState(project.start_date || new Date().toISOString().slice(0, 10));
  const [crewSize, setCrewSize] = useState(2);
  const [hoursPerDay, setHoursPerDay] = useState(8);
  const [skipWeekends, setSkipWeekends] = useState(true);
  const [busy, setBusy] = useState(false);

  /** Roughly estimate man-hours for one etapa.
   *  - unit=hr/HR/hour → quantity itself is hours
   *  - other units → assume $85/hr labor rate → hours = (qty * unit_price) / 85 (only for labor kind)
   *  - material steps → 0 (no labor time)
   */
  const stepHours = (st) => {
    if ((st.kind || "labor") !== "labor") return 0;
    const u = (st.unit || "").toLowerCase();
    if (u === "hr" || u === "h" || u === "hour" || u === "hours") return st.quantity || 0;
    const total = (st.quantity || 0) * (st.unit_price || 0);
    return total > 0 ? total / 85 : (st.quantity || 0);   // fallback: 1 unit = 1 hour
  };

  const addDays = (dateStr, days) => {
    const d = new Date(dateStr);
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      if (!skipWeekends || (d.getDay() !== 0 && d.getDay() !== 6)) added += 1;
    }
    return d.toISOString().slice(0, 10);
  };

  const preview = phases.map((ph) => {
    const sc = phaseScope(ph);
    const hours = (sc?.steps || []).reduce((a, s) => a + stepHours(s), 0);
    const dailyCapacity = crewSize * hoursPerDay;
    const days = Math.max(1, Math.ceil(hours / dailyCapacity));
    return { ph, sc, hours, days };
  });

  let cursor = startDate;
  const scheduled = preview.map(({ ph, sc, hours, days }) => {
    const start = cursor;
    const end = addDays(start, days);
    cursor = end;
    return { ph, sc, hours, days, start, end };
  });

  const applySchedule = async () => {
    setBusy(true);
    try {
      // Also set project.start_date to keep it in sync
      await api.patch(`/projects/${project.id}`, { start_date: startDate });
      let stepsUpdated = 0;
      for (const { ph, sc, start, end } of scheduled) {
        await api.patch(`/projects/${project.id}/phases/${ph.id}`, { start_date: start, end_date: end });
        // ---- Distribute step dates inside the phase window ----
        if (sc && (sc.steps || []).length) {
          const laborSteps = sc.steps.filter((s) => (s.kind || "labor") === "labor");
          const materialSteps = sc.steps.filter((s) => s.kind === "material");
          const dailyCapacity = crewSize * hoursPerDay;
          // Sequentially schedule labor steps by hours, capped at phase end
          let laborCursor = start;
          const nextSteps = sc.steps.map((s) => ({ ...s }));
          for (const st of laborSteps) {
            const h = stepHours(st);
            const d = Math.max(1, Math.ceil(h / dailyCapacity));
            const sStart = laborCursor;
            let sEnd = addDays(sStart, d);
            if (sEnd > end) sEnd = end;   // never exceed phase end
            const target = nextSteps.find((x) => x.id === st.id);
            if (target) { target.start_date = sStart; target.end_date = sEnd; }
            laborCursor = sEnd;
          }
          // Materials: delivery/arrival on phase start (single day)
          for (const st of materialSteps) {
            const target = nextSteps.find((x) => x.id === st.id);
            if (target) { target.start_date = start; target.end_date = start; }
          }
          await api.patch(`/projects/${project.id}/scopes/${sc.id}`, { steps: nextSteps });
          stepsUpdated += laborSteps.length + materialSteps.length;
        }
      }
      toast.success(`Schedule applied — ${scheduled.length} phase(s), ${stepsUpdated} step(s)`);
      onCall && onCall(async () => {});
      setOpen(false);
    } catch (e) {
      toast.error(errMsg(e, "Failed"));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid="prj-wbs-schedule-btn"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold px-3 py-1.5 rounded"
      >
        <Clock className="w-3.5 h-3.5" /> Auto-Schedule
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-white text-slate-900 rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="prj-schedule-wizard">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white">
              <h2 className="text-base font-semibold flex items-center gap-2">
                <Clock className="w-4 h-4" /> Auto-Schedule
              </h2>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>

            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500">Start date</label>
                  <input type="date" value={startDate}
                    data-testid="prj-schedule-start"
                    onChange={(e) => setStartDate(e.target.value)}
                    className={inputCls} />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500">Crew size</label>
                  <input type="number" min="1" max="20" value={crewSize}
                    onChange={(e) => setCrewSize(parseInt(e.target.value, 10) || 1)}
                    className={inputCls} />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500">Hours/day</label>
                  <input type="number" min="1" max="16" value={hoursPerDay}
                    onChange={(e) => setHoursPerDay(parseInt(e.target.value, 10) || 8)}
                    className={inputCls} />
                </div>
              </div>

              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={skipWeekends} onChange={(e) => setSkipWeekends(e.target.checked)} />
                Skip weekends
              </label>

              <div className="text-[11px] text-slate-500 border border-blue-200 bg-blue-50 rounded-lg p-2.5">
                <b>How we compute hours:</b> Labor steps with unit=hr use the qty directly.
                Other units (EA/SF/LF): total / $85/hr (avg labor rate). Phases run sequentially.
              </div>

              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="grid grid-cols-12 gap-2 items-center px-3 py-2 bg-slate-50 text-[10px] font-bold uppercase text-slate-500">
                  <div className="col-span-4">Phase</div>
                  <div className="col-span-2 text-right">Hours</div>
                  <div className="col-span-1 text-right">Days</div>
                  <div className="col-span-2 text-right">Start</div>
                  <div className="col-span-3 text-right">End</div>
                </div>
                {scheduled.map(({ ph, hours, days, start, end }) => (
                  <div key={ph.id} className="grid grid-cols-12 gap-2 items-center px-3 py-1.5 border-t border-slate-100 text-xs" data-testid={`prj-schedule-row-${ph.id}`}>
                    <div className="col-span-4 truncate font-semibold">{ph.name}</div>
                    <div className="col-span-2 text-right tabular-nums">{hours.toFixed(1)}h</div>
                    <div className="col-span-1 text-right tabular-nums font-semibold">{days}d</div>
                    <div className="col-span-2 text-right tabular-nums">{start}</div>
                    <div className="col-span-3 text-right tabular-nums font-semibold text-emerald-700">{end}</div>
                  </div>
                ))}
                <div className="grid grid-cols-12 gap-2 items-center px-3 py-2 border-t border-slate-200 bg-slate-50 text-xs font-bold">
                  <div className="col-span-4">TOTAL</div>
                  <div className="col-span-2 text-right tabular-nums">{scheduled.reduce((a, x) => a + x.hours, 0).toFixed(1)}h</div>
                  <div className="col-span-1 text-right tabular-nums">{scheduled.reduce((a, x) => a + x.days, 0)}d</div>
                  <div className="col-span-2 text-right tabular-nums text-slate-500">{startDate}</div>
                  <div className="col-span-3 text-right tabular-nums text-emerald-700">{scheduled.length > 0 ? scheduled[scheduled.length - 1].end : "—"}</div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 sticky bottom-0 bg-white">
              <button onClick={() => setOpen(false)} disabled={busy}
                className="text-xs px-3 py-1.5 rounded border border-slate-300">Cancel</button>
              <button
                data-testid="prj-schedule-apply"
                onClick={applySchedule} disabled={busy || scheduled.length === 0}
                className="text-xs px-4 py-1.5 rounded bg-emerald-600 text-white font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Apply schedule to {scheduled.length} phase{scheduled.length !== 1 ? "s" : ""}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ================= WBS + Schedule (Fase → Etapas) ================= */

function WBSTab({ project, canManage, canFinance, onCall, onPay }) {
  const [editingPhaseId, setEditingPhaseId] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => {
    const m = {};
    (project.phases || []).forEach((p) => { m[p.id] = true; });
    return m;
  });

  const phases = project.phases || [];
  const scopes = project.scopes || [];

  // For each phase, find its paired scope (1:1 with AI-generated projects)
  const phaseScope = (ph) => scopes.find((s) => s.phase_id === ph.id) || null;
  const orphanScopes = scopes.filter((s) => !phases.some((p) => p.id === s.phase_id));

  const phaseCost = (ph) => {
    const sc = phaseScope(ph);
    if (!sc) return 0;
    const stepCost = (sc.steps || []).reduce((a, st) => a + (st.quantity || 0) * (st.unit_price || 0), 0);
    const sub = sc.subcontract ? (sc.subcontract.billed_to_client || 0) : 0;
    return stepCost + sub;
  };

  const stepPct = (st) => Math.max(0, Math.min(100, st.completed ? 100 : (st.completed_pct || 0)));

  const phaseProgress = (ph) => {
    const sc = phaseScope(ph);
    if (!sc) return ph.progress_pct || 0;
    const steps = sc.steps || [];
    if (!steps.length) return sc.subcontract ? (sc.subcontract.progress_pct || 0) : (ph.progress_pct || 0);
    let total = 0, done = 0;
    steps.forEach((s) => {
      const t = (s.quantity || 0) * (s.unit_price || 0);
      total += t; done += t * (stepPct(s) / 100);
    });
    return total > 0 ? Math.round((done / total) * 100) : (ph.progress_pct || 0);
  };

  const patchPhase = (phase, patch) =>
    onCall(() => api.patch(`/projects/${project.id}/phases/${phase.id}`, patch));
  const patchScope = (scope, patch) =>
    onCall(() => api.patch(`/projects/${project.id}/scopes/${scope.id}`, patch));
  const patchStep = (scope, stepId, patch) => {
    const nextSteps = (scope.steps || []).map((s) => s.id === stepId ? { ...s, ...patch } : s);
    return patchScope(scope, { steps: nextSteps });
  };

  // Timeline range
  const allDates = [];
  phases.forEach((p) => { if (p.start_date) allDates.push(p.start_date); if (p.end_date) allDates.push(p.end_date); });
  scopes.forEach((sc) => (sc.steps || []).forEach((st) => {
    if (st.start_date) allDates.push(st.start_date);
    if (st.end_date) allDates.push(st.end_date);
  }));
  const today = new Date().toISOString().slice(0, 10);
  const minDate = allDates.length ? allDates.reduce((a, b) => (a < b ? a : b)) : today;
  const maxDate = allDates.length ? allDates.reduce((a, b) => (a > b ? a : b)) : today;
  const timelineStart = new Date(minDate);
  const timelineEnd = new Date(maxDate);
  const totalDays = Math.max(30, Math.ceil((timelineEnd - timelineStart) / (24 * 3600 * 1000)) + 14);
  const barPct = (start, end) => {
    if (!start || !end) return null;
    const s = new Date(start); const e = new Date(end);
    const startOff = Math.max(0, (s - timelineStart) / (24 * 3600 * 1000));
    const width = Math.max(1, (e - s) / (24 * 3600 * 1000));
    return { left: (startOff / totalDays) * 100, width: (width / totalDays) * 100 };
  };

  if (phases.length === 0 && orphanScopes.length === 0) {
    return (
      <div className="text-center py-10 space-y-4" data-testid="prj-wbs-empty">
        <div className="text-xs text-slate-400">No phases or etapas yet — generate the WBS with AI, or add phases manually.</div>
        {canManage && (
          <button
            data-testid="prj-wbs-ai-empty"
            onClick={() => setAiOpen(true)}
            className="inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow"
          >
            <Sparkles className="w-4 h-4" /> Generate with AI
          </button>
        )}
        {aiOpen && (
          <ProjectAIAssistModal project={project} onClose={() => setAiOpen(false)} onApplied={() => { setAiOpen(false); onCall(); }} />
        )}
      </div>
    );
  }

  const renderEtapa = (st, sc, kind) => {
    const stotal = (st.quantity || 0) * (st.unit_price || 0);
    const pct = stepPct(st);
    const progAmt = stotal * (pct / 100);
    const remAmt = stotal - progAmt;
    const isMat = kind === "material";
    const kindColor = isMat ? "bg-amber-400" : "bg-blue-400";
    return (
      <div key={st.id} className="grid grid-cols-12 gap-2 items-center px-3 py-1.5 border-b border-slate-100 text-[11px] hover:bg-slate-50/40"
           data-testid={`prj-wbs-step-${st.id}`}>
        <div className="col-span-5 pl-8 flex items-center gap-2 min-w-0">
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${kindColor}`} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-slate-800 font-medium">
              <span className="truncate">{st.description}</span>
            </div>
            <div className="text-[10px] text-slate-400 tabular-nums">{st.quantity} {st.unit} × {money(st.unit_price)}</div>
          </div>
        </div>
        <div className="col-span-2 text-right tabular-nums font-semibold text-slate-800">{money(stotal)}</div>
        <div className="col-span-2 flex items-center justify-end gap-1.5">
          {canManage ? (
            <div className="relative">
              <input type="text" inputMode="numeric" value={pct}
                data-testid={`prj-wbs-step-progress-${st.id}`}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, "");
                  const v = Math.max(0, Math.min(100, parseInt(raw, 10) || 0));
                  patchStep(sc, st.id, { completed_pct: v, completed: v === 100 });
                }}
                className="w-14 text-center text-xs rounded border border-slate-300 pl-1 pr-4 py-0.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
              <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">%</span>
            </div>
          ) : (
            <span className="text-xs font-semibold tabular-nums text-emerald-700 w-8 text-right">{pct}%</span>
          )}
          <span className="text-[11px] tabular-nums text-emerald-700 font-semibold min-w-[60px] text-right">{money(progAmt)}</span>
        </div>
        <div className="col-span-3 text-right tabular-nums">
          <span className="text-amber-700 font-semibold">{money(remAmt)}</span>
          <span className="text-[10px] text-amber-600 ml-1.5">{(100 - pct).toFixed(0)}%</span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="prj-wbs-tab">
      <div className="flex items-center justify-between bg-slate-900 text-white rounded-lg p-3 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-300 font-bold">WBS &amp; Progress</div>
          <div className="text-xs text-slate-300 mt-0.5">Each phase is a work package. Enter progress % per line to see how much is done and how much remains.</div>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <button
              data-testid="prj-wbs-ai-btn"
              onClick={() => setAiOpen(true)}
              className="inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow"
            >
              <Sparkles className="w-3.5 h-3.5" /> Generate with AI
            </button>
          )}
          <ScheduleWizard project={project} onCall={onCall} phases={phases} phaseScope={phaseScope} />
        </div>
      </div>

      {aiOpen && (
        <ProjectAIAssistModal
          project={project}
          onClose={() => setAiOpen(false)}
          onApplied={() => { setAiOpen(false); onCall(); }}
        />
      )}

      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <div className="grid grid-cols-12 gap-2 items-center px-3 py-2 border-b border-slate-200 bg-slate-50 text-[10px] font-bold uppercase text-slate-500 tracking-wider">
          <div className="col-span-5">Phase / Step</div>
          <div className="col-span-2 text-right">Value</div>
          <div className="col-span-2 text-right">Progress</div>
          <div className="col-span-3 text-right">Remaining</div>
        </div>

        {/* Contract-wide summary row — sum across all phases */}
        {(() => {
          const totalValue = phases.reduce((a, ph) => a + phaseCost(ph), 0);
          const totalProgress = phases.reduce((a, ph) => a + phaseCost(ph) * (phaseProgress(ph) / 100), 0);
          const totalRemaining = totalValue - totalProgress;
          const progPct = totalValue > 0 ? (totalProgress / totalValue) * 100 : 0;
          const remPct = 100 - progPct;
          return (
            <div className="grid grid-cols-12 gap-2 items-center px-3 py-2 border-b-2 border-slate-300 bg-slate-100"
                 data-testid="prj-wbs-contract-summary">
              <div className="col-span-5 flex items-center gap-1.5 min-w-0">
                <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center shrink-0">Σ</span>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-bold uppercase text-slate-500 tracking-wider">Contract Total</div>
                  <div className="text-sm font-bold text-slate-900 truncate">All phases combined</div>
                </div>
              </div>
              <div className="col-span-2 text-right text-sm font-bold tabular-nums text-slate-900">{money(totalValue)}</div>
              <div className="col-span-2 flex items-center justify-end gap-1.5">
                <span className="text-xs font-bold tabular-nums text-emerald-700 w-10 text-right">{progPct.toFixed(0)}%</span>
                <span className="text-sm tabular-nums text-emerald-700 font-bold min-w-[70px] text-right">{money(totalProgress)}</span>
              </div>
              <div className="col-span-3 text-right tabular-nums">
                <span className="text-amber-700 font-bold text-sm">{money(totalRemaining)}</span>
                <span className="text-[11px] text-amber-600 ml-1.5 font-semibold">{remPct.toFixed(0)}%</span>
              </div>
            </div>
          );
        })()}

        {phases.map((ph) => {
          const sc = phaseScope(ph);
          const cost = phaseCost(ph);
          const prog = phaseProgress(ph);
          const bar = barPct(ph.start_date, ph.end_date);
          const isOpen = expanded[ph.id] !== false;
          const isEditing = editingPhaseId === ph.id;

          return (
            <React.Fragment key={ph.id}>
              <div className="grid grid-cols-12 gap-2 items-center px-3 py-2 border-b border-slate-200 bg-gradient-to-r from-slate-900 to-slate-800 text-white"
                   data-testid={`prj-wbs-phase-${ph.id}`}>
                <div className="col-span-5 flex items-center gap-1.5 min-w-0">
                  <button
                    onClick={() => setExpanded({ ...expanded, [ph.id]: !isOpen })}
                    className="p-0.5 rounded hover:bg-white/10"
                    data-testid={`prj-wbs-phase-toggle-${ph.id}`}
                  >
                    <ChevronRight className={`w-4 h-4 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                  </button>
                  <Layers className="w-3.5 h-3.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-bold uppercase opacity-80">
                      Phase {ph.order || "?"}
                      {sc?.trade && <span className="ml-1.5 px-1 bg-white/20 rounded">{sc.trade}</span>}
                      {sc?.subcontract && <span className="ml-1 px-1 bg-amber-400 text-amber-900 rounded">SUB</span>}
                    </div>
                    <div className="text-sm font-bold truncate">{ph.name}</div>
                  </div>
                  {canManage && (
                    <div className="flex gap-0.5 shrink-0">
                      {sc && (
                        <button
                          data-testid={`prj-wbs-phase-edit-${ph.id}`}
                          onClick={() => setEditingPhaseId(isEditing ? null : ph.id)}
                          className="text-[10px] font-semibold text-white bg-white/10 hover:bg-white/20 px-1.5 py-0.5 rounded"
                        >{isEditing ? "Close" : "Edit"}</button>
                      )}
                      <button
                        data-testid={`prj-wbs-phase-delete-${ph.id}`}
                        onClick={() => {
                          if (window.confirm(`Delete phase "${ph.name}" and all its etapas?`)) {
                            onCall(() => api.del(`/projects/${project.id}/phases/${ph.id}`));
                          }
                        }}
                        className="p-1 text-white/70 hover:text-red-300"
                      ><Trash2 className="w-3 h-3" /></button>
                    </div>
                  )}
                </div>
                <div className="col-span-2 text-right text-sm font-bold tabular-nums">{money(cost)}</div>
                <div className="col-span-2 flex items-center justify-end gap-1.5">
                  {canManage && sc ? (
                    <div className="relative">
                      <input type="text" inputMode="numeric" value={prog}
                        data-testid={`prj-wbs-phase-progress-${ph.id}`}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^0-9]/g, "");
                          const v = Math.max(0, Math.min(100, parseInt(raw, 10) || 0));
                          // Apply pct uniformly to all steps of the paired scope
                          const nextSteps = (sc.steps || []).map((s) => ({ ...s, completed_pct: v, completed: v === 100 }));
                          patchScope(sc, { steps: nextSteps });
                        }}
                        className="w-14 text-center text-xs rounded bg-white/10 border border-white/30 text-white pl-1 pr-4 py-0.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                      <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-white/60 pointer-events-none">%</span>
                    </div>
                  ) : (
                    <span className="text-xs font-semibold tabular-nums w-8 text-right">{prog}%</span>
                  )}
                  <span className="text-[11px] tabular-nums text-emerald-300 font-semibold min-w-[60px] text-right">{money(cost * (prog / 100))}</span>
                </div>
                <div className="col-span-3 text-right tabular-nums">
                  <span className="text-amber-300 font-semibold text-sm">{money(cost * (1 - prog / 100))}</span>
                  <span className="text-[10px] text-amber-200 ml-1.5">{(100 - prog).toFixed(0)}%</span>
                </div>
              </div>

              {/* Steps — separated into LABOR and MATERIAL */}
              {isOpen && sc && (() => {
                const laborSteps = (sc.steps || []).filter((s) => (s.kind || "labor") === "labor");
                const materialSteps = (sc.steps || []).filter((s) => s.kind === "material");
                const pendingMaterial = materialSteps.filter((s) => !s.purchase_request_id);
                return (
                  <React.Fragment>
                    {/* LABOR sub-header */}
                    {laborSteps.length > 0 && (
                      <div className="grid grid-cols-12 gap-2 items-center px-3 py-1 border-b border-slate-100 bg-blue-50/40 text-[10px] font-bold uppercase text-blue-800 tracking-wider">
                        <div className="col-span-12 pl-8"><i className="fa-solid fa-hammer mr-1"></i> Labor ({laborSteps.length})</div>
                      </div>
                    )}
                    {laborSteps.map((st) => renderEtapa(st, sc, "labor"))}

                    {/* MATERIAL sub-header */}
                    {materialSteps.length > 0 && (
                      <div className="grid grid-cols-12 gap-2 items-center px-3 py-1 border-b border-slate-100 bg-amber-50/50 text-[10px] font-bold uppercase text-amber-800 tracking-wider">
                        <div className="col-span-8 pl-8"><i className="fa-solid fa-box mr-1"></i> Material ({materialSteps.length})</div>
                        {canManage && pendingMaterial.length > 0 && (
                          <div className="col-span-4 text-right">
                            <button
                              data-testid={`prj-wbs-send-purchasing-${sc.id}`}
                              onClick={async () => {
                                if (!window.confirm(`Send ${pendingMaterial.length} material item(s) to Purchasing?`)) return;
                                try {
                                  const res = await api.post(`/projects/${project.id}/scopes/${sc.id}/send-to-purchasing`, {});
                                  toast.success(`Material Request ${res.number} created (${res.items} items)`);
                                  onCall(async () => {});
                                } catch (e) {
                                  toast.error(errMsg(e, "Failed"));
                                }
                              }}
                              className="text-[10px] font-bold bg-amber-500 hover:bg-amber-600 text-white px-2 py-0.5 rounded inline-flex items-center gap-1"
                            >
                              <Send className="w-2.5 h-2.5" /> Send {pendingMaterial.length} to Purchasing
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {materialSteps.map((st) => renderEtapa(st, sc, "material"))}
                  </React.Fragment>
                );
              })()}

              {/* Inline edit panel */}
              {isEditing && sc && (
                <div className="px-3 py-3 bg-blue-50/40 border-b border-blue-200" data-testid={`prj-wbs-phase-editor-${ph.id}`}>
                  <PhaseInlineEditor
                    phase={ph}
                    scope={sc}
                    project={project}
                    canFinance={canFinance}
                    onCall={onCall}
                    onPay={() => onPay(sc)}
                    onClose={() => setEditingPhaseId(null)}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}

        {orphanScopes.length > 0 && (
          <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800">
            ⚠ {orphanScopes.length} scope(s) without phase — legacy data. Delete them from the old Scopes view.
          </div>
        )}

        {canManage && (
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50">
            <div className="col-span-12">
              <button
                data-testid="prj-wbs-add-phase"
                onClick={async () => {
                  const name = window.prompt("New phase — name?");
                  if (!name) return;
                  onCall(() => api.post(`/projects/${project.id}/phases`, {
                    name, order: phases.length + 1,
                  }));
                }}
                className="text-xs text-blue-600 hover:text-blue-800 font-semibold inline-flex items-center gap-1"
              ><Plus className="w-3.5 h-3.5" /> Add phase</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function GanttBar({ bar, progress, color }) {
  if (!bar) {
    return <div className="text-[10px] text-slate-300 italic text-center">no dates</div>;
  }
  return (
    <div className="relative h-3 bg-slate-100 rounded overflow-hidden">
      <div
        className={`absolute top-0 h-full ${color} rounded opacity-60`}
        style={{ left: `${bar.left}%`, width: `${bar.width}%` }}
      />
      <div
        className="absolute top-0 h-full bg-emerald-500 rounded"
        style={{ left: `${bar.left}%`, width: `${(bar.width * (progress || 0)) / 100}%` }}
      />
    </div>
  );
}

/* ================= Schedule Gantt Tab — visual timeline of phases + steps ================= */
function ScheduleGanttTab({ project }) {
  const phases = project.phases || [];
  const scopes = project.scopes || [];
  const phaseScope = (ph) => scopes.find((s) => s.phase_id === ph.id) || null;
  const ganttRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const exportPDF = async () => {
    if (!ganttRef.current) return;
    setExporting(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const canvas = await html2canvas(ganttRef.current, {
        scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      // Landscape A4 (297 × 210 mm)
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      // Title
      pdf.setFontSize(14); pdf.setFont("helvetica", "bold");
      pdf.text(`Project Schedule — ${project.name || ""}`, margin, margin + 4);
      pdf.setFontSize(9); pdf.setFont("helvetica", "normal");
      pdf.setTextColor(90);
      pdf.text(`${project.client_name || ""}${project.property_name ? " · " + project.property_name : ""}`, margin, margin + 9);
      pdf.text(`Generated: ${new Date().toISOString().slice(0, 10)}`, pageW - margin, margin + 4, { align: "right" });
      // Fit image below title
      const availW = pageW - margin * 2;
      const availH = pageH - margin - 15;
      const imgW = canvas.width, imgH = canvas.height;
      let drawW = availW, drawH = (imgH / imgW) * availW;
      if (drawH > availH) { drawH = availH; drawW = (imgW / imgH) * availH; }
      pdf.addImage(imgData, "PNG", margin, margin + 13, drawW, drawH);
      pdf.save(`schedule_${project.name || project.id}_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("Gantt exported");
    } catch (e) {
      toast.error(errMsg(e, "Export failed"));
    } finally { setExporting(false); }
  };

  // Collect all dates to derive the timeline range
  const allDates = [];
  phases.forEach((p) => { if (p.start_date) allDates.push(p.start_date); if (p.end_date) allDates.push(p.end_date); });
  scopes.forEach((sc) => (sc.steps || []).forEach((st) => {
    if (st.start_date) allDates.push(st.start_date);
    if (st.end_date) allDates.push(st.end_date);
  }));

  if (allDates.length === 0) {
    return (
      <div className="text-center text-xs text-slate-400 py-10" data-testid="prj-schedule-empty">
        No schedule yet — open WBS &amp; Schedule → Auto-Schedule to generate a timeline.
      </div>
    );
  }

  const minDate = allDates.reduce((a, b) => (a < b ? a : b));
  const maxDate = allDates.reduce((a, b) => (a > b ? a : b));
  const timelineStart = new Date(minDate);
  const timelineEnd = new Date(maxDate);
  const totalDays = Math.max(7, Math.ceil((timelineEnd - timelineStart) / (24 * 3600 * 1000)) + 3);

  const barPct = (start, end) => {
    if (!start || !end) return null;
    const s = new Date(start); const e = new Date(end);
    const startOff = Math.max(0, (s - timelineStart) / (24 * 3600 * 1000));
    const width = Math.max(1, (e - s) / (24 * 3600 * 1000) + 1);
    return { left: (startOff / totalDays) * 100, width: (width / totalDays) * 100 };
  };

  // Build week ticks for the timeline header
  const ticks = [];
  const tickStep = totalDays <= 30 ? 2 : totalDays <= 90 ? 7 : 14;   // days between ticks
  for (let d = 0; d <= totalDays; d += tickStep) {
    const dt = new Date(timelineStart);
    dt.setDate(dt.getDate() + d);
    ticks.push({ label: dt.toISOString().slice(5, 10), pct: (d / totalDays) * 100 });
  }

  const today = new Date();
  const todayOff = (today - timelineStart) / (24 * 3600 * 1000);
  const todayPct = todayOff >= 0 && todayOff <= totalDays ? (todayOff / totalDays) * 100 : null;

  const stepPct = (st) => Math.max(0, Math.min(100, st.completed ? 100 : (st.completed_pct || 0)));
  const phaseProgress = (ph) => {
    const sc = phaseScope(ph);
    if (!sc) return ph.progress_pct || 0;
    const steps = sc.steps || [];
    if (!steps.length) return ph.progress_pct || 0;
    let total = 0, done = 0;
    steps.forEach((s) => {
      const t = (s.quantity || 0) * (s.unit_price || 0);
      total += t; done += t * (stepPct(s) / 100);
    });
    return total > 0 ? Math.round((done / total) * 100) : (ph.progress_pct || 0);
  };

  const days = (a, b) => Math.max(1, Math.ceil((new Date(b) - new Date(a)) / (24 * 3600 * 1000)) + 1);

  return (
    <div className="space-y-3" data-testid="prj-schedule-tab">
      <div className="flex items-center justify-between bg-slate-900 text-white rounded-lg p-3 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-300 font-bold">Project Schedule</div>
          <div className="text-xs text-slate-300 mt-0.5">
            {minDate} → {maxDate} · {totalDays} day span
          </div>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex gap-3 text-[10px]">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-2.5 rounded-sm bg-blue-400" /> Phase</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-2.5 rounded-sm bg-blue-300" /> Labor step</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-2.5 rounded-sm bg-amber-400" /> Material</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-2.5 rounded-sm bg-emerald-500" /> Progress</span>
          </div>
          <button
            data-testid="prj-schedule-export-pdf"
            onClick={exportPDF} disabled={exporting}
            className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold px-3 py-1.5 rounded disabled:opacity-50"
          >
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Export PDF
          </button>
        </div>
      </div>

      <div ref={ganttRef} className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        {/* Timeline header */}
        <div className="grid grid-cols-12 border-b border-slate-200 bg-slate-50">
          <div className="col-span-4 px-3 py-2 text-[10px] font-bold uppercase text-slate-500 border-r border-slate-200">
            Phase / Step
          </div>
          <div className="col-span-8 relative h-9">
            {ticks.map((t, i) => (
              <div key={i} className="absolute top-0 h-full border-l border-slate-200"
                   style={{ left: `${t.pct}%` }}>
                <div className="text-[9px] font-semibold text-slate-500 pl-1 pt-1 tabular-nums">{t.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Rows */}
        {phases.map((ph) => {
          const sc = phaseScope(ph);
          const phBar = barPct(ph.start_date, ph.end_date);
          const prog = phaseProgress(ph);
          const laborSteps = sc ? (sc.steps || []).filter((s) => (s.kind || "labor") === "labor") : [];
          const materialSteps = sc ? (sc.steps || []).filter((s) => s.kind === "material") : [];
          return (
            <React.Fragment key={ph.id}>
              {/* Phase row */}
              <div className="grid grid-cols-12 border-b border-slate-200 bg-gradient-to-r from-slate-900 to-slate-800 text-white"
                   data-testid={`prj-schedule-phase-${ph.id}`}>
                <div className="col-span-4 px-3 py-2 border-r border-slate-700 flex items-center gap-2 min-w-0">
                  <Layers className="w-3.5 h-3.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[9px] font-bold uppercase opacity-70">Phase {ph.order || "?"} · {prog}%</div>
                    <div className="text-xs font-bold truncate">{ph.name}</div>
                  </div>
                  {ph.start_date && ph.end_date && (
                    <div className="text-[9px] tabular-nums opacity-80 shrink-0">
                      {ph.start_date.slice(5)} → {ph.end_date.slice(5)}
                      <div className="text-right opacity-70">{days(ph.start_date, ph.end_date)}d</div>
                    </div>
                  )}
                </div>
                <div className="col-span-8 relative h-9">
                  {/* Weekly ticks background */}
                  {ticks.map((t, i) => (
                    <div key={i} className="absolute top-0 h-full border-l border-white/10" style={{ left: `${t.pct}%` }} />
                  ))}
                  {/* Today marker */}
                  {todayPct !== null && (
                    <div className="absolute top-0 h-full border-l-2 border-red-400 z-10" style={{ left: `${todayPct}%` }} />
                  )}
                  {phBar && (
                    <div className="absolute top-1/2 -translate-y-1/2 h-4 rounded"
                         style={{ left: `${phBar.left}%`, width: `${phBar.width}%` }}>
                      <div className="absolute inset-0 bg-blue-400 opacity-70 rounded" />
                      <div className="absolute top-0 h-full bg-emerald-500 rounded"
                           style={{ width: `${prog}%` }} />
                    </div>
                  )}
                </div>
              </div>

              {/* Labor step rows */}
              {laborSteps.map((st) => {
                const stBar = barPct(st.start_date, st.end_date);
                const pct = stepPct(st);
                return (
                  <div key={st.id} className="grid grid-cols-12 border-b border-slate-100 hover:bg-slate-50/40"
                       data-testid={`prj-schedule-step-${st.id}`}>
                    <div className="col-span-4 px-3 py-1.5 border-r border-slate-100 flex items-center gap-2 pl-8 min-w-0">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-blue-400" />
                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] text-slate-800 truncate font-medium">{st.description}</div>
                        <div className="text-[9px] text-slate-400 tabular-nums">{st.quantity} {st.unit} · {pct}%</div>
                      </div>
                      {st.start_date && st.end_date && (
                        <div className="text-[9px] tabular-nums text-slate-400 shrink-0">
                          {st.start_date.slice(5)} → {st.end_date.slice(5)}
                        </div>
                      )}
                    </div>
                    <div className="col-span-8 relative h-7">
                      {ticks.map((t, i) => (
                        <div key={i} className="absolute top-0 h-full border-l border-slate-100" style={{ left: `${t.pct}%` }} />
                      ))}
                      {todayPct !== null && (
                        <div className="absolute top-0 h-full border-l-2 border-red-400" style={{ left: `${todayPct}%` }} />
                      )}
                      {stBar && (
                        <div className="absolute top-1/2 -translate-y-1/2 h-2.5 rounded"
                             style={{ left: `${stBar.left}%`, width: `${stBar.width}%` }}>
                          <div className="absolute inset-0 bg-blue-300 opacity-70 rounded" />
                          <div className="absolute top-0 h-full bg-emerald-500 rounded"
                               style={{ width: `${pct}%` }} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Material step rows */}
              {materialSteps.map((st) => {
                const stBar = barPct(st.start_date, st.end_date);
                const pct = stepPct(st);
                return (
                  <div key={st.id} className="grid grid-cols-12 border-b border-slate-100 hover:bg-amber-50/40"
                       data-testid={`prj-schedule-step-${st.id}`}>
                    <div className="col-span-4 px-3 py-1.5 border-r border-slate-100 flex items-center gap-2 pl-8 min-w-0">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-amber-400" />
                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] text-slate-800 truncate font-medium">{st.description}</div>
                        <div className="text-[9px] text-slate-400 tabular-nums">
                          {st.quantity} {st.unit}
                          {st.purchase_status && <span className="ml-1 uppercase font-bold text-amber-600">· {st.purchase_status}</span>}
                        </div>
                      </div>
                      {st.start_date && (
                        <div className="text-[9px] tabular-nums text-slate-400 shrink-0">
                          {st.start_date.slice(5)}
                        </div>
                      )}
                    </div>
                    <div className="col-span-8 relative h-7">
                      {ticks.map((t, i) => (
                        <div key={i} className="absolute top-0 h-full border-l border-slate-100" style={{ left: `${t.pct}%` }} />
                      ))}
                      {todayPct !== null && (
                        <div className="absolute top-0 h-full border-l-2 border-red-400" style={{ left: `${todayPct}%` }} />
                      )}
                      {stBar && (
                        <div className="absolute top-1/2 -translate-y-1/2 h-2.5 rounded"
                             style={{ left: `${stBar.left}%`, width: `${Math.max(stBar.width, 1.2)}%` }}>
                          <div className="absolute inset-0 bg-amber-400 opacity-80 rounded" />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>

      {todayPct !== null && (
        <div className="text-[10px] text-slate-400">
          <span className="inline-block w-3 border-t-2 border-red-400 align-middle mr-1"></span>
          Red line = today ({today.toISOString().slice(0, 10)})
        </div>
      )}
    </div>
  );
}

/* ================= Financials Tab — forecast + budget × real ================= */
function MarkupEditor({ project }) {
  const [labor, setLabor] = useState(project.labor_markup_pct ?? 0);
  const [material, setMaterial] = useState(project.material_markup_pct ?? 0);
  const [overall, setOverall] = useState(project.overall_markup_pct ?? 0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const dirty = labor !== (project.labor_markup_pct ?? 0)
    || material !== (project.material_markup_pct ?? 0)
    || overall !== (project.overall_markup_pct ?? 0);

  const recompute = async () => {
    setBusy(true);
    try {
      // First persist the markup values on the project
      await api.patch(`/projects/${project.id}`, {
        labor_markup_pct: parseFloat(labor) || 0,
        material_markup_pct: parseFloat(material) || 0,
        overall_markup_pct: parseFloat(overall) || 0,
      });
      // Then recompute Contract $ = cost × (1 + markup)
      const r = await api.post(`/projects/${project.id}/recompute-pricing`, {});
      setResult(r);
      toast.success(`Recomputed: contract ${money(r.sell)} · margin ${money(r.margin)} (${r.margin_pct.toFixed(1)}%)`);
      // Ask parent to refresh to pick up new contract_value + Financials
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error(errMsg(e, "Failed to recompute"));
    } finally { setBusy(false); }
  };

  return (
    <div className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white rounded-lg p-3" data-testid="prj-markup-editor">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-bold uppercase text-purple-800 tracking-wider inline-flex items-center gap-1.5">
          <TrendingUp className="w-3.5 h-3.5" /> Estimate markup — Cost × (1 + markup) = Sell price
        </div>
        <button
          data-testid="prj-recompute-pricing-btn"
          onClick={recompute} disabled={busy}
          className="text-[11px] font-bold bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
          {dirty ? "Save markup & recompute Contract $" : "Recompute Contract $ from cost × markup"}
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <label className="text-[10px] font-bold uppercase text-slate-500">Labor markup %</label>
          <input type="number" step="1" min="0" max="200" value={labor}
            data-testid="prj-fin-markup-labor"
            onChange={(e) => setLabor(parseFloat(e.target.value) || 0)}
            className={`${inputCls} text-right tabular-nums`} />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-slate-500">Material markup %</label>
          <input type="number" step="1" min="0" max="200" value={material}
            data-testid="prj-fin-markup-material"
            onChange={(e) => setMaterial(parseFloat(e.target.value) || 0)}
            className={`${inputCls} text-right tabular-nums`} />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-slate-500">Overall markup %</label>
          <input type="number" step="1" min="0" max="200" value={overall}
            data-testid="prj-fin-markup-overall"
            onChange={(e) => setOverall(parseFloat(e.target.value) || 0)}
            className={`${inputCls} text-right tabular-nums`} />
        </div>
      </div>
      {result && (
        <div className="mt-2 text-[11px] text-slate-600 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div><b className="text-slate-500">Cost:</b> <span className="tabular-nums">{money(result.cost)}</span></div>
          <div><b className="text-emerald-700">Sell:</b> <span className="tabular-nums font-semibold text-emerald-700">{money(result.sell)}</span></div>
          <div><b className={result.margin >= 0 ? "text-emerald-700" : "text-red-600"}>Margin:</b> <span className={`tabular-nums font-semibold ${result.margin >= 0 ? "text-emerald-700" : "text-red-600"}`}>{money(result.margin)} ({result.margin_pct.toFixed(1)}%)</span></div>
        </div>
      )}
    </div>
  );
}

function FinancialsTab({ project }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("monthly");   // "monthly" | "weekly"

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/projects/${project.id}/financials`)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) { setData(null); toast.error(errMsg(e, "Failed to load financials")); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [project.id, project.updated_at]);

  if (loading) return <div className="text-center text-xs text-slate-400 py-10"><Loader2 className="inline-block w-4 h-4 animate-spin mr-1" /> Loading financials…</div>;
  if (!data) return <div className="text-center text-xs text-slate-400 py-10">No financial data available.</div>;

  const rows = view === "monthly" ? data.monthly : data.weekly;
  const rowKey = view === "monthly" ? "month" : "week_start";

  // Compute max for scaling bar widths
  const maxVal = Math.max(1, ...rows.map((r) => Math.max(
    (r.labor_forecast || 0) + (r.material_forecast || 0) + (r.equipment_forecast || 0) + (r.subs_forecast || 0),
    r.revenue_forecast || 0,
    (r.labor_real || 0) + (r.material_real || 0) + (r.equipment_real || 0) + (r.subs_real || 0),
    r.revenue_real || 0
  )));

  const CATS = [
    { key: "labor",     label: "Labor",     color: "bg-blue-500",    lightColor: "bg-blue-200" },
    { key: "material",  label: "Material",  color: "bg-amber-500",   lightColor: "bg-amber-200" },
    { key: "equipment", label: "Equipment", color: "bg-cyan-500",    lightColor: "bg-cyan-200" },
    { key: "subs",      label: "Subs",      color: "bg-indigo-500",  lightColor: "bg-indigo-200" },
    { key: "revenue",   label: "Revenue",   color: "bg-emerald-500", lightColor: "bg-emerald-200" },
  ];

  const totalCost = (t) => (t.labor.forecast || 0) + (t.material.forecast || 0) + (t.equipment?.forecast || 0) + (t.subs.forecast || 0);
  const realCost  = (t) => (t.labor.real || 0)     + (t.material.real || 0)     + (t.equipment?.real || 0)     + (t.subs.real || 0);

  const exportPDF = async () => {
    try {
      const { jsPDF } = await import("jspdf");
      const autoTable = (await import("jspdf-autotable")).default;

      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const M = 14;   // margin

      // ============ Header ============
      doc.setFillColor(15, 23, 42);   // slate-900
      doc.rect(0, 0, pageW, 32, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold").setFontSize(16);
      doc.text("Project Financial Forecast", M, 14);
      doc.setFont("helvetica", "normal").setFontSize(9);
      doc.text(`Generated ${new Date().toISOString().slice(0, 10)}`, pageW - M, 14, { align: "right" });
      doc.setFontSize(11).setFont("helvetica", "bold");
      doc.text(project.name || "Untitled project", M, 22);
      doc.setFont("helvetica", "normal").setFontSize(9);
      doc.text(`${project.number || ""} · ${project.client_name || ""}${project.property_name ? " · " + project.property_name : ""}`, M, 28);

      // ============ KPI Cards ============
      const T = data.totals;
      const costTotal = T.cost?.forecast || (T.labor.forecast + T.material.forecast + (T.equipment?.forecast || 0) + T.subs.forecast);
      const margin = T.margin?.forecast ?? (T.revenue.forecast - costTotal);
      const marginPct = T.margin?.forecast_pct ?? (T.revenue.forecast > 0 ? margin / T.revenue.forecast * 100 : 0);
      const kpiY = 40;
      const cardW = (pageW - M * 2 - 6) / 4;
      const kpis = [
        ["Contract price", `$${T.revenue.forecast.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, [16, 185, 129]],
        ["Cost forecast",  `$${costTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, [245, 158, 11]],
        ["Gross margin",   `$${margin.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin >= 0 ? [16, 185, 129] : [239, 68, 68]],
        ["Margin %",       `${marginPct.toFixed(1)}%`, margin >= 0 ? [16, 185, 129] : [239, 68, 68]],
      ];
      kpis.forEach((k, i) => {
        const x = M + i * (cardW + 2);
        doc.setDrawColor(226, 232, 240).setFillColor(248, 250, 252);
        doc.roundedRect(x, kpiY, cardW, 20, 2, 2, "FD");
        doc.setFont("helvetica", "bold").setFontSize(8).setTextColor(100, 116, 139);
        doc.text(k[0].toUpperCase(), x + 2, kpiY + 5);
        doc.setFontSize(13).setTextColor(...k[2]);
        doc.text(k[1], x + 2, kpiY + 13);
      });

      // ============ Cost breakdown table ============
      let cursorY = kpiY + 26;
      doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(15, 23, 42);
      doc.text("Cost breakdown", M, cursorY);
      cursorY += 2;
      autoTable(doc, {
        startY: cursorY,
        margin: { left: M, right: M },
        head: [["Category", "Budget", "Forecast", "Real"]],
        body: [
          ["Labor",    `$${T.labor.budget.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,     `$${T.labor.forecast.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,     `$${T.labor.real.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
          ["Material", `$${T.material.budget.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,  `$${T.material.forecast.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,  `$${T.material.real.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
          ["Equipment", "$0.00", `$${(T.equipment?.forecast || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, `$${(T.equipment?.real || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
          ["Subcontractors", `$${T.subs.budget.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, `$${T.subs.forecast.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, `$${T.subs.real.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
          [{ content: "COST TOTAL", styles: { fontStyle: "bold" } },
           { content: `$${(T.labor.budget + T.material.budget + T.subs.budget).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, styles: { fontStyle: "bold" } },
           { content: `$${costTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, styles: { fontStyle: "bold" } },
           { content: `$${(T.cost?.real || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, styles: { fontStyle: "bold" } }],
        ],
        theme: "striped",
        headStyles: { fillColor: [51, 65, 85], textColor: 255, fontSize: 9, fontStyle: "bold" },
        bodyStyles: { fontSize: 9, textColor: [30, 41, 59] },
        columnStyles: { 0: { cellWidth: 45 }, 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        didParseCell: (data) => {
          if (data.row.index === 4) data.cell.styles.fillColor = [226, 232, 240];   // TOTAL row
        },
      });

      // ============ Monthly cash-flow ============
      cursorY = doc.lastAutoTable.finalY + 8;
      doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(15, 23, 42);
      doc.text("Monthly forecast — cost × revenue × margin", M, cursorY);
      cursorY += 2;
      const monthRows = data.monthly.map((r) => {
        const costFc = (r.labor_forecast || 0) + (r.material_forecast || 0) + (r.equipment_forecast || 0) + (r.subs_forecast || 0);
        const marginFc = (r.revenue_forecast || 0) - costFc;
        return [
          r.month,
          `$${(r.labor_forecast || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          `$${(r.material_forecast || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          `$${(r.equipment_forecast || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          `$${(r.subs_forecast || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          `$${costFc.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          `$${(r.revenue_forecast || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          { content: `$${marginFc.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, styles: { textColor: marginFc >= 0 ? [16, 185, 129] : [239, 68, 68], fontStyle: "bold" } },
        ];
      });
      const monthTotals = [
        { content: "TOTAL", styles: { fontStyle: "bold" } },
        { content: `$${T.labor.forecast.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, styles: { fontStyle: "bold" } },
        { content: `$${T.material.forecast.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, styles: { fontStyle: "bold" } },
        { content: `$${(T.equipment?.forecast || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, styles: { fontStyle: "bold" } },
        { content: `$${T.subs.forecast.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, styles: { fontStyle: "bold" } },
        { content: `$${costTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, styles: { fontStyle: "bold" } },
        { content: `$${T.revenue.forecast.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, styles: { fontStyle: "bold" } },
        { content: `$${margin.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, styles: { fontStyle: "bold", textColor: margin >= 0 ? [16, 185, 129] : [239, 68, 68] } },
      ];
      autoTable(doc, {
        startY: cursorY,
        margin: { left: M, right: M },
        head: [["Month", "Labor", "Material", "Equipment", "Subs", "Cost total", "Revenue", "Margin"]],
        body: [...monthRows, monthTotals],
        theme: "striped",
        headStyles: { fillColor: [51, 65, 85], textColor: 255, fontSize: 8.5, fontStyle: "bold" },
        bodyStyles: { fontSize: 8.5, textColor: [30, 41, 59], halign: "right" },
        columnStyles: { 0: { halign: "left", fontStyle: "bold" } },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        didParseCell: (data) => {
          if (data.row.index === monthRows.length) data.cell.styles.fillColor = [226, 232, 240];
        },
      });

      // ============ Weekly labor detail (paycheck-friendly) ============
      cursorY = doc.lastAutoTable.finalY + 8;
      // Add page break if not enough room
      if (cursorY > 240) { doc.addPage(); cursorY = M; }
      doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(15, 23, 42);
      doc.text("Weekly labor forecast (Monday start)", M, cursorY);
      doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(100, 116, 139);
      doc.text("Use this to size weekly payroll during the project window.", M, cursorY + 4);
      cursorY += 6;
      const weekRows = data.weekly.map((r) => [
        r.week_start,
        `$${(r.labor_forecast || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        `$${(r.material_forecast || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        `$${(r.equipment_forecast || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        `$${(r.subs_forecast || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        `$${((r.labor_forecast || 0) + (r.material_forecast || 0) + (r.equipment_forecast || 0) + (r.subs_forecast || 0)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      ]);
      autoTable(doc, {
        startY: cursorY,
        margin: { left: M, right: M },
        head: [["Week (Mon)", "Labor", "Material", "Equipment", "Subs", "Total"]],
        body: weekRows,
        theme: "striped",
        headStyles: { fillColor: [51, 65, 85], textColor: 255, fontSize: 8.5, fontStyle: "bold" },
        bodyStyles: { fontSize: 8.5, textColor: [30, 41, 59], halign: "right" },
        columnStyles: { 0: { halign: "left", fontStyle: "bold" } },
        alternateRowStyles: { fillColor: [248, 250, 252] },
      });

      // ============ Footer with notes ============
      const finalY = doc.lastAutoTable.finalY + 6;
      if (finalY < 280) {
        doc.setFont("helvetica", "italic").setFontSize(7.5).setTextColor(148, 163, 184);
        doc.text("Sources: Labor & Material from scheduled WBS steps · Subs spread across phase windows · Revenue from milestone due dates (fallback to contract_value when no milestones exist).",
                 M, finalY, { maxWidth: pageW - M * 2 });
      }

      const fname = `financial_forecast_${(project.name || project.id).replace(/[^a-z0-9]/gi, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`;
      doc.save(fname);
      toast.success("Financial forecast PDF exported");
    } catch (e) {
      toast.error(errMsg(e, "Export failed"));
    }
  };

  return (
    <div className="space-y-4" data-testid="prj-financials-tab">
      {/* Markup editor + recompute — lets user tune the pricing after creation
          (e.g. client rejects the proposal → PM lowers markup and re-emits) */}
      <MarkupEditor project={project} />

      {/* HEADER + KPIs */}
      <div className="bg-slate-900 text-white rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-300 font-bold">Contract price</div>
          <div className="text-lg font-bold tabular-nums text-emerald-300">{money(data.totals.revenue.forecast)}</div>
          <div className="text-[10px] text-slate-400 tabular-nums">Invoiced: {money(data.totals.revenue.real)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-300 font-bold">Cost forecast</div>
          <div className="text-lg font-bold tabular-nums text-amber-300">{money(data.totals.cost?.forecast || totalCost(data.totals))}</div>
          <div className="text-[10px] text-slate-400 tabular-nums">Real so far: {money(data.totals.cost?.real || realCost(data.totals))}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-300 font-bold">Gross margin (fc)</div>
          {(() => {
            const margin = data.totals.margin?.forecast ?? (data.totals.revenue.forecast - totalCost(data.totals));
            const pct = data.totals.margin?.forecast_pct ?? (data.totals.revenue.forecast > 0 ? (margin / data.totals.revenue.forecast) * 100 : 0);
            return <>
              <div className={`text-lg font-bold tabular-nums ${margin >= 0 ? "text-emerald-300" : "text-red-300"}`} data-testid="prj-fin-margin">{money(margin)}</div>
              <div className={`text-[10px] tabular-nums ${margin >= 0 ? "text-emerald-400" : "text-red-400"}`}>{pct.toFixed(1)}% of revenue</div>
            </>;
          })()}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-300 font-bold">View</div>
          <div className="flex gap-1 mt-1">
            <button
              data-testid="prj-fin-view-monthly"
              onClick={() => setView("monthly")}
              className={`text-[10px] font-bold px-2.5 py-1 rounded ${view === "monthly" ? "bg-white text-slate-900" : "bg-slate-700 text-slate-300"}`}
            >Monthly</button>
            <button
              data-testid="prj-fin-view-weekly"
              onClick={() => setView("weekly")}
              className={`text-[10px] font-bold px-2.5 py-1 rounded ${view === "weekly" ? "bg-white text-slate-900" : "bg-slate-700 text-slate-300"}`}
            >Weekly</button>
          </div>
          <button
            data-testid="prj-fin-export-pdf"
            onClick={exportPDF}
            className="mt-2 inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] font-bold px-2.5 py-1 rounded"
          >
            <Download className="w-3 h-3" /> Export PDF
          </button>
        </div>
      </div>

      {/* Budget × Forecast × Real by category */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase text-slate-500">
          <div className="col-span-3">Category</div>
          <div className="col-span-2 text-right">Budget</div>
          <div className="col-span-2 text-right">Forecast</div>
          <div className="col-span-2 text-right">Real</div>
          <div className="col-span-3">Real vs Forecast</div>
        </div>
        {CATS.map((c) => {
          const t = data.totals[c.key];
          const overRun = t.forecast > 0 ? (t.real / t.forecast) * 100 : 0;
          return (
            <div key={c.key} className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-slate-100 items-center text-xs" data-testid={`prj-fin-cat-${c.key}`}>
              <div className="col-span-3 flex items-center gap-2 font-semibold">
                <span className={`w-3 h-3 rounded-sm ${c.color}`} />
                {c.label}
              </div>
              <div className="col-span-2 text-right tabular-nums text-slate-500">{money(t.budget)}</div>
              <div className="col-span-2 text-right tabular-nums">{money(t.forecast)}</div>
              <div className="col-span-2 text-right tabular-nums font-semibold">{money(t.real)}</div>
              <div className="col-span-3 flex items-center gap-2">
                <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden">
                  <div
                    className={`h-full ${overRun > 110 ? "bg-red-500" : overRun > 85 ? "bg-amber-500" : "bg-emerald-500"} rounded`}
                    style={{ width: `${Math.min(overRun, 100)}%` }}
                  />
                </div>
                <div className={`text-[10px] font-bold tabular-nums w-10 text-right ${overRun > 110 ? "text-red-600" : overRun > 85 ? "text-amber-600" : "text-emerald-600"}`}>
                  {overRun.toFixed(0)}%
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Per-Phase breakdown — Cost vs Revenue with variance */}
      {(data.phases && data.phases.length > 0) && (
        <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="prj-fin-phase-table">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase text-slate-600 flex items-center justify-between">
            <span>By phase — Budget vs Real (Cost & Revenue)</span>
            {data.unallocated_labor_real > 0 && (
              <span className="text-[10px] font-semibold text-amber-600" title="Hours not yet allocated by the analyst">
                Unallocated labor: {money(data.unallocated_labor_real)}
              </span>
            )}
          </div>
          <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-slate-50/60 border-b border-slate-200 text-[10px] font-bold uppercase text-slate-500">
            <div className="col-span-2">Phase</div>
            <div className="col-span-1 text-right">Contract $</div>
            <div className="col-span-1 text-right">Budget Cost</div>
            <div className="col-span-1 text-right">Real Labor</div>
            <div className="col-span-1 text-right">Real Material</div>
            <div className="col-span-1 text-right">Real Subs</div>
            <div className="col-span-1 text-right">Total Real</div>
            <div className="col-span-1 text-right">Revenue Billed</div>
            <div className="col-span-1 text-right">Variance $</div>
            <div className="col-span-2">Real vs Budget</div>
          </div>
          {data.phases.map((p) => {
            const bt = p.budget?.total || 0;
            const rt = p.real?.total || 0;
            const pct = bt > 0 ? (rt / bt * 100) : 0;
            return (
              <div key={p.phase_id} className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-slate-100 items-center text-xs" data-testid={`prj-fin-phase-${p.phase_id}`}>
                <div className="col-span-2 min-w-0">
                  <div className="font-semibold truncate">{p.phase_name}</div>
                  {(p.start_date || p.end_date) && (
                    <div className="text-[10px] text-slate-400 tabular-nums">{p.start_date || "?"} → {p.end_date || "?"}</div>
                  )}
                </div>
                <div className="col-span-1 text-right tabular-nums text-slate-700">{money(p.contract_amount)}</div>
                <div className="col-span-1 text-right tabular-nums text-slate-500">{money(bt)}</div>
                <div className="col-span-1 text-right tabular-nums">{money(p.real?.labor || 0)}</div>
                <div className="col-span-1 text-right tabular-nums">{money(p.real?.material || 0)}</div>
                <div className="col-span-1 text-right tabular-nums">{money(p.real?.subs || 0)}</div>
                <div className="col-span-1 text-right tabular-nums font-semibold">{money(rt)}</div>
                <div className="col-span-1 text-right tabular-nums text-emerald-700 font-semibold">{money(p.revenue_billed || 0)}</div>
                <div className={`col-span-1 text-right tabular-nums font-bold ${p.variance_cost >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                  {(p.variance_cost >= 0 ? "+" : "") + money(p.variance_cost)}
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden">
                    <div className={`h-full ${pct > 110 ? "bg-red-500" : pct > 85 ? "bg-amber-500" : "bg-emerald-500"} rounded`}
                      style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                  <div className={`text-[10px] font-bold tabular-nums w-10 text-right ${pct > 110 ? "text-red-600" : pct > 85 ? "text-amber-600" : "text-emerald-600"}`}>
                    {bt > 0 ? `${pct.toFixed(0)}%` : "—"}
                  </div>
                </div>
              </div>
            );
          })}
          <div className="px-4 py-1.5 bg-slate-50 border-t border-slate-200 text-[10px] text-slate-500 italic">
            Real Labor uses Time Allocations set by the back-office analyst. Unallocated hours still count towards the project total (not per-phase). Revenue Billed = approved/sent/paid invoices.
          </div>
        </div>
      )}

      {/* Time-series bar chart */}
      <div className="border border-slate-200 rounded-lg">
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="text-xs font-bold uppercase text-slate-600">
            {view === "monthly" ? "Monthly" : "Weekly"} cash-flow forecast × real
          </div>
          <div className="flex gap-3 text-[10px]">
            {CATS.map((c) => (
              <span key={c.key} className="inline-flex items-center gap-1"><span className={`w-2.5 h-2.5 rounded-sm ${c.color}`} /> {c.label}</span>
            ))}
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="p-8 text-center text-xs text-slate-400">No scheduled data yet — run Auto-Schedule first.</div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid={`prj-fin-${view}-list`}>
            {rows.map((r) => {
              const costFc = (r.labor_forecast || 0) + (r.material_forecast || 0) + (r.equipment_forecast || 0) + (r.subs_forecast || 0);
              const revFc = r.revenue_forecast;
              const costReal = (r.labor_real || 0) + (r.material_real || 0) + (r.equipment_real || 0) + (r.subs_real || 0);
              const revReal = r.revenue_real;
              return (
                <div key={r[rowKey]} className="grid grid-cols-12 gap-2 items-center px-4 py-2 hover:bg-slate-50/50">
                  <div className="col-span-2 text-xs font-bold tabular-nums text-slate-700">{r[rowKey]}</div>
                  <div className="col-span-10 space-y-1">
                    {/* Cost bar (stacked) */}
                    <div className="flex items-center gap-2 text-[10px]">
                      <div className="w-10 text-right text-slate-400">cost fc</div>
                      <div className="flex-1 h-3.5 bg-slate-100 rounded overflow-hidden flex">
                        <div className="h-full bg-blue-500"    style={{ width: `${((r.labor_forecast || 0) / maxVal) * 100}%` }}     title={`Labor ${money(r.labor_forecast)}`} />
                        <div className="h-full bg-amber-500"   style={{ width: `${((r.material_forecast || 0) / maxVal) * 100}%` }}  title={`Material ${money(r.material_forecast)}`} />
                        <div className="h-full bg-cyan-500"    style={{ width: `${((r.equipment_forecast || 0) / maxVal) * 100}%` }} title={`Equipment ${money(r.equipment_forecast || 0)}`} />
                        <div className="h-full bg-indigo-500"  style={{ width: `${((r.subs_forecast || 0) / maxVal) * 100}%` }}      title={`Subs ${money(r.subs_forecast)}`} />
                      </div>
                      <div className="w-20 text-right font-semibold tabular-nums">{money(costFc)}</div>
                    </div>
                    {/* Cost real (dashed lighter) */}
                    {costReal > 0 && (
                      <div className="flex items-center gap-2 text-[10px]">
                        <div className="w-10 text-right text-slate-400">real</div>
                        <div className="flex-1 h-2.5 bg-slate-50 rounded overflow-hidden flex border border-dashed border-slate-300">
                          <div className="h-full bg-blue-300"    style={{ width: `${((r.labor_real || 0) / maxVal) * 100}%` }} />
                          <div className="h-full bg-amber-300"   style={{ width: `${((r.material_real || 0) / maxVal) * 100}%` }} />
                          <div className="h-full bg-cyan-300"    style={{ width: `${((r.equipment_real || 0) / maxVal) * 100}%` }} />
                          <div className="h-full bg-indigo-300"  style={{ width: `${((r.subs_real || 0) / maxVal) * 100}%` }} />
                        </div>
                        <div className="w-20 text-right font-semibold tabular-nums text-slate-600">{money(costReal)}</div>
                      </div>
                    )}
                    {/* Revenue */}
                    {(revFc > 0 || revReal > 0) && (
                      <div className="flex items-center gap-2 text-[10px]">
                        <div className="w-10 text-right text-emerald-500 font-semibold">rev</div>
                        <div className="flex-1 h-3.5 bg-slate-100 rounded overflow-hidden">
                          <div className="h-full bg-emerald-500" style={{ width: `${(revFc / maxVal) * 100}%` }} title={`Revenue fc ${money(revFc)}`} />
                        </div>
                        <div className="w-20 text-right font-semibold tabular-nums text-emerald-700">{money(revFc)}</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="text-[10px] text-slate-400 space-y-0.5">
        <div><b>Forecast sources:</b> Labor &amp; Material — from scheduled WBS steps (start/end dates × cost). Subs — spread evenly across the phase window. Revenue — from milestones due-date × amount.</div>
        <div><b>Real sources:</b> Labor — time_entries × user hourly_rate. Material — MRs linked to the project. Subs — accumulated paid_to_sub (or sub invoices when present). Revenue — invoiced/paid milestones.</div>
      </div>
    </div>
  );
}

/* ================= Documents Tab — contracts, insurance, sub W-9, permits ================= */
const DOC_CATEGORIES = [
  { key: "contract",     label: "Client Contract",   color: "bg-blue-100 text-blue-800 border-blue-200" },
  { key: "sub_contract", label: "Sub Contract",      color: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  { key: "insurance",    label: "Insurance Cert.",   color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { key: "w9",           label: "W-9 / Tax",         color: "bg-amber-100 text-amber-800 border-amber-200" },
  { key: "estimate",     label: "Estimate / Proposal", color: "bg-purple-100 text-purple-800 border-purple-200" },
  { key: "permit",       label: "Permit",            color: "bg-rose-100 text-rose-800 border-rose-200" },
  { key: "photo",        label: "Site Photo",        color: "bg-cyan-100 text-cyan-800 border-cyan-200" },
  { key: "other",        label: "Other",             color: "bg-slate-100 text-slate-700 border-slate-200" },
];

function DocumentsTab({ project, canManage, onCall }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ name: "", category: "contract", notes: "", file: null });

  const reload = () => {
    setLoading(true);
    api.get(`/projects/${project.id}/documents`)
      .then((r) => setDocs(r || []))
      .catch((e) => toast.error(errMsg(e, "Failed to load documents")))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [project.id]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("File too large (max ~15MB)"); return; }
    setForm((s) => ({ ...s, file: f, name: s.name || f.name.replace(/\.[^.]+$/, "") }));
  };

  const upload = async () => {
    if (!form.file) { toast.error("Pick a file first"); return; }
    if (!form.name.trim()) { toast.error("Give the document a name"); return; }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          await api.post(`/projects/${project.id}/documents`, {
            name: form.name,
            category: form.category,
            filename: form.file.name,
            mime: form.file.type,
            size: form.file.size,
            content_b64: reader.result,   // data URL — backend stores as-is
            notes: form.notes,
          });
          toast.success(`Uploaded "${form.name}"`);
          setForm({ name: "", category: form.category, notes: "", file: null });
          reload();
        } catch (e) {
          toast.error(errMsg(e, "Upload failed"));
        } finally { setUploading(false); }
      };
      reader.readAsDataURL(form.file);
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
      setUploading(false);
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"? This cannot be undone.`)) return;
    try {
      await api.del(`/projects/${project.id}/documents/${doc.id}`);
      toast.success("Deleted");
      reload();
    } catch (e) { toast.error(errMsg(e, "Failed to delete")); }
  };

  const download = async (doc) => {
    try {
      const full = await api.get(`/projects/${project.id}/documents/${doc.id}`);
      const b64 = (full.content_b64 || "").split(",").pop();
      const bin = atob(b64);
      const buf = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
      const blob = new Blob([buf], { type: full.mime || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = full.filename || full.name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(errMsg(e, "Download failed")); }
  };

  const filtered = filter === "all" ? docs : docs.filter((d) => d.category === filter);
  const bytesReadable = (n) => n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(2)} MB`;
  const catCount = (k) => docs.filter((d) => d.category === k).length;

  return (
    <div className="space-y-4" data-testid="prj-documents-tab">
      {/* Upload widget */}
      {canManage && (
        <div className="bg-slate-900 text-white rounded-lg p-4 space-y-3">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-300 flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Add a document
          </div>
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-4">
              <label className="text-[10px] font-bold uppercase text-slate-400">Document name</label>
              <input value={form.name}
                data-testid="prj-doc-name"
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Master Service Agreement 2026"
                className="w-full rounded-lg border border-slate-600 bg-slate-800 text-white px-3 py-2 text-sm" />
            </div>
            <div className="col-span-3">
              <label className="text-[10px] font-bold uppercase text-slate-400">Category</label>
              <select
                data-testid="prj-doc-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full rounded-lg border border-slate-600 bg-slate-800 text-white px-3 py-2 text-sm"
              >
                {DOC_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div className="col-span-3">
              <label className="text-[10px] font-bold uppercase text-slate-400">File</label>
              <input type="file" onChange={handleFile}
                data-testid="prj-doc-file"
                className="w-full text-xs text-slate-200 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-bold file:bg-slate-700 file:text-white hover:file:bg-slate-600" />
            </div>
            <div className="col-span-2 flex items-end">
              <button
                data-testid="prj-doc-upload-btn"
                onClick={upload}
                disabled={uploading || !form.file || !form.name.trim()}
                className="w-full inline-flex items-center justify-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold px-3 py-2 rounded disabled:opacity-50"
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Upload
              </button>
            </div>
            <div className="col-span-12">
              <input value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional notes (e.g. expires 12/2026)"
                className="w-full rounded-lg border border-slate-600 bg-slate-800 text-white px-3 py-1.5 text-xs" />
            </div>
          </div>
          {form.file && (
            <div className="text-[10px] text-slate-400">
              Selected: <b>{form.file.name}</b> · {bytesReadable(form.file.size)} · {form.file.type || "unknown"}
            </div>
          )}
        </div>
      )}

      {/* Category filter chips */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <button onClick={() => setFilter("all")}
          className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${filter === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
          All ({docs.length})
        </button>
        {DOC_CATEGORIES.map((c) => (
          <button key={c.key} onClick={() => setFilter(c.key)}
            className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${filter === c.key ? "bg-slate-900 text-white border-slate-900" : `${c.color} hover:opacity-80`}`}>
            {c.label} ({catCount(c.key)})
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center text-xs text-slate-400 py-10"><Loader2 className="inline-block w-4 h-4 animate-spin mr-1" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-xs text-slate-400 py-10 border-2 border-dashed border-slate-200 rounded-lg">
          {docs.length === 0 ? "No documents yet — upload the client contract, sub certificates or any file above." : "No documents in this category."}
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase text-slate-500">
            <div className="col-span-4">Name</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-2">Uploaded by</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-1 text-right">Size</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {filtered.map((d) => {
            const cat = DOC_CATEGORIES.find((c) => c.key === d.category) || DOC_CATEGORIES[DOC_CATEGORIES.length - 1];
            return (
              <div key={d.id} className="grid grid-cols-12 gap-2 items-center px-3 py-2 border-b border-slate-100 text-xs hover:bg-slate-50/50" data-testid={`prj-doc-row-${d.id}`}>
                <div className="col-span-4 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{d.name}</div>
                  {d.notes && <div className="text-[10px] text-slate-500 truncate italic">{d.notes}</div>}
                  <div className="text-[10px] text-slate-400 truncate">{d.filename}</div>
                </div>
                <div className="col-span-2">
                  <span className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${cat.color}`}>{cat.label}</span>
                </div>
                <div className="col-span-2 text-slate-700 truncate">{d.uploaded_by_name}</div>
                <div className="col-span-2 text-slate-500 tabular-nums text-[11px]">{(d.uploaded_at || "").slice(0, 10)}</div>
                <div className="col-span-1 text-right tabular-nums text-slate-500 text-[11px]">{bytesReadable(d.size || 0)}</div>
                <div className="col-span-1 text-right flex items-center justify-end gap-1">
                  <button
                    data-testid={`prj-doc-download-${d.id}`}
                    onClick={() => download(d)}
                    title="Download"
                    className="p-1 rounded hover:bg-slate-100 text-slate-600"><Download className="w-3.5 h-3.5" /></button>
                  {canManage && (
                    <button
                      data-testid={`prj-doc-delete-${d.id}`}
                      onClick={() => remove(d)}
                      title="Delete"
                      className="p-1 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="text-[10px] text-slate-400 italic">
        Documents are stored inside the project record. Max ~15MB per file. For heavier files (large blueprints, videos) we can enable object storage later.
      </div>
    </div>
  );
}

function PhaseInlineEditor({ phase, scope, project, canFinance, onCall, onPay, onClose }) {
  const [phName, setPhName] = useState(phase.name);
  const [scopeName, setScopeName] = useState(scope.name);
  const [trade, setTrade] = useState(scope.trade || "");
  const [steps, setSteps] = useState(() => (scope.steps || []).map((s) => ({ ...s })));
  // Support both new `subcontracts` list and legacy single `subcontract`
  const [subs, setSubs] = useState(() => {
    if (Array.isArray(scope.subcontracts) && scope.subcontracts.length) return scope.subcontracts.map((s) => ({ ...s }));
    if (scope.subcontract) return [{ ...scope.subcontract }];
    return [];
  });
  const [subCrm, setSubCrm] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openQuotesFor, setOpenQuotesFor] = useState(null);

  useEffect(() => {
    api.get("/subcontractors").then((r) => setSubCrm(r || [])).catch(() => setSubCrm([]));
  }, []);

  const patchStep = (i, patch) => setSteps(steps.map((x, j) => j === i ? { ...x, ...patch } : x));
  const removeStep = (i) => setSteps(steps.filter((_, j) => j !== i));

  const addSub = () => {
    if (subs.length >= 2) { toast.error("Maximum 2 subcontractors per scope"); return; }
    setSubs([...subs, { subcontractor_id: null, subcontractor_name: "", contract_amount: 0, billed_to_client: 0, progress_pct: 0, paid_to_sub: 0, invoices: [] }]);
  };
  const patchSub = (i, p) => setSubs(subs.map((x, j) => j === i ? { ...x, ...p } : x));
  const removeSub = (i) => setSubs(subs.filter((_, j) => j !== i));
  const pickSub = (i, subId) => {
    const s = subCrm.find((x) => x.id === subId);
    patchSub(i, s ? { subcontractor_id: s.id, subcontractor_name: s.name } : { subcontractor_id: null, subcontractor_name: "" });
  };

  const save = async () => {
    setBusy(true);
    try {
      // Update phase name if changed
      if (phName !== phase.name) {
        await onCall(() => api.patch(`/projects/${project.id}/phases/${phase.id}`, { name: phName }));
      }
      const cleanSubs = subs.filter((s) => s && s.subcontractor_name);
      await onCall(() => api.patch(`/projects/${project.id}/scopes/${scope.id}`, {
        name: scopeName,
        trade,
        steps: steps.filter((s) => s.description).map((s) => ({
          id: s.id, description: s.description,
          quantity: parseFloat(s.quantity) || 0, unit: s.unit || "unit",
          unit_price: parseFloat(s.unit_price) || 0,
          completed: s.completed || false, completed_pct: parseFloat(s.completed_pct) || 0,
          start_date: s.start_date || null, end_date: s.end_date || null,
          kind: s.kind || "labor",
          quotes: s.quotes || [],
          purchase_status: s.purchase_status || null,
          purchase_request_id: s.purchase_request_id || null,
        })),
        // Persist both fields — new list + legacy single (first sub) for older readers
        subcontracts: cleanSubs,
        subcontract: cleanSubs[0] || null,
      }));
      onClose();
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2 text-xs">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="col-span-2">
          <label className="text-[10px] font-bold uppercase text-slate-500">Phase name</label>
          <input value={phName} onChange={(e) => { setPhName(e.target.value); setScopeName(e.target.value); }}
            data-testid={`prj-wbs-phase-name-${phase.id}`}
            className="w-full rounded border border-slate-300 px-2 py-1 text-xs" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-slate-500">Trade</label>
          <input value={trade} onChange={(e) => setTrade(e.target.value)}
            placeholder="electrical, plumbing..."
            className="w-full rounded border border-slate-300 px-2 py-1 text-xs" />
        </div>
      </div>

      <div>
        <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Steps ({steps.length})</div>
        <div className="grid grid-cols-12 gap-1 mb-1 text-[9px] font-bold uppercase text-slate-400 px-1">
          <div className="col-span-1">Type</div>
          <div className="col-span-4">Description</div>
          <div className="col-span-1 text-right">Qty</div>
          <div className="col-span-1">Un</div>
          <div className="col-span-2 text-right">Price</div>
          <div className="col-span-1 text-center">%</div>
          <div className="col-span-1">Quotes</div>
          <div className="col-span-1"></div>
        </div>
        {steps.map((s, i) => (
          <React.Fragment key={i}>
            <div className="grid grid-cols-12 gap-1 mb-1">
              <select value={s.kind || "labor"}
                data-testid={`prj-wbs-etapa-kind-${phase.id}-${i}`}
                onChange={(e) => patchStep(i, { kind: e.target.value })}
                className={`col-span-1 rounded border border-slate-200 px-1 py-1 text-[10px] font-bold uppercase ${
                  s.kind === "material" ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700"
                }`}>
                <option value="labor">Labor</option>
                <option value="material">Material</option>
                <option value="equipment">Equipment</option>
              </select>
              <input placeholder="Description" value={s.description || ""}
                data-testid={`prj-wbs-etapa-desc-${phase.id}-${i}`}
                onChange={(e) => patchStep(i, { description: e.target.value })}
                className="col-span-4 rounded border border-slate-200 px-2 py-1 text-xs" />
              <input type="number" placeholder="Qty" value={s.quantity || 0}
                onChange={(e) => patchStep(i, { quantity: e.target.value })}
                className="col-span-1 rounded border border-slate-200 px-2 py-1 text-xs text-right" />
              <input placeholder="Un" value={s.unit || ""}
                onChange={(e) => patchStep(i, { unit: e.target.value })}
                className="col-span-1 rounded border border-slate-200 px-2 py-1 text-xs" />
              <input type="number" placeholder="Price" value={s.unit_price || 0}
                data-testid={`prj-wbs-etapa-price-${phase.id}-${i}`}
                onChange={(e) => patchStep(i, { unit_price: e.target.value })}
                className="col-span-2 rounded border border-slate-200 px-2 py-1 text-xs text-right" />
              <input type="number" min="0" max="100" value={s.completed_pct || 0}
                onChange={(e) => patchStep(i, { completed_pct: parseInt(e.target.value, 10) || 0 })}
                className="col-span-1 rounded border border-slate-200 px-1 py-1 text-xs text-center" />
              <div className="col-span-1 flex items-center justify-center">
                {s.kind === "material" && s.id ? (
                  <button
                    data-testid={`prj-wbs-etapa-quotes-${phase.id}-${i}`}
                    onClick={() => setOpenQuotesFor(openQuotesFor === s.id ? null : s.id)}
                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                      (s.quotes || []).length > 0 ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-500"
                    } hover:bg-indigo-200`}
                    title={`${(s.quotes || []).length}/3 quotes`}
                  >{(s.quotes || []).length}/3</button>
                ) : (
                  <span className="text-[10px] text-slate-300">—</span>
                )}
              </div>
              <button onClick={() => removeStep(i)}
                data-testid={`prj-wbs-etapa-remove-${phase.id}-${i}`}
                className="col-span-1 p-1 text-slate-400 hover:text-red-600"
              ><Trash2 className="w-3 h-3" /></button>
            </div>
            {openQuotesFor === s.id && s.id && (
              <QuotesPanel project={project} scope={scope} step={s}
                onDone={() => setOpenQuotesFor(null)}
                onCall={onCall} />
            )}
          </React.Fragment>
        ))}
        <div className="flex gap-2">
          <button
            data-testid={`prj-wbs-etapa-add-labor-${phase.id}`}
            onClick={() => setSteps([...steps, { description: "", quantity: 1, unit: "hr", unit_price: 0, kind: "labor" }])}
            className="text-xs font-semibold text-blue-600 inline-flex items-center gap-1"
          ><Plus className="w-3 h-3" /> Labor step</button>
          <button
            data-testid={`prj-wbs-etapa-add-material-${phase.id}`}
            onClick={() => setSteps([...steps, { description: "", quantity: 1, unit: "EA", unit_price: 0, kind: "material" }])}
            className="text-xs font-semibold text-amber-600 inline-flex items-center gap-1"
          ><Plus className="w-3 h-3" /> Material step</button>
        </div>
      </div>

      <div>
        <div className="text-[10px] font-bold uppercase text-slate-500 mb-1 flex items-center justify-between">
          <span>Subcontractors {subs.length > 0 && <span className="ml-1 text-slate-400">({subs.length}/2)</span>}</span>
          {subs.length < 2 && (
            <button
              data-testid={`prj-wbs-add-sub-${phase.id}`}
              onClick={addSub}
              className="text-[10px] font-semibold text-indigo-600 inline-flex items-center gap-1 normal-case"
            ><Plus className="w-3 h-3" /> Add sub</button>
          )}
        </div>
        {subs.length === 0 && (
          <div className="text-[11px] text-slate-400 italic">No subcontractors on this scope.</div>
        )}
        {subs.map((sub, idx) => (
          <div key={idx} className="border border-indigo-100 bg-indigo-50/30 rounded p-2 space-y-1 mb-1.5" data-testid={`prj-wbs-sub-${phase.id}-${idx}`}>
            <div className="flex items-center gap-1.5">
              <select
                data-testid={`prj-wbs-sub-picker-${phase.id}-${idx}`}
                value={sub.subcontractor_id || ""}
                onChange={(e) => pickSub(idx, e.target.value)}
                className="flex-1 rounded border border-slate-200 px-2 py-1 text-xs"
              >
                <option value="">— pick from Subcontractors CRM —</option>
                {subCrm.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <input placeholder="or type name" value={sub.subcontractor_name || ""}
                onChange={(e) => patchSub(idx, { subcontractor_name: e.target.value })}
                className="flex-1 rounded border border-slate-200 px-2 py-1 text-xs" />
              <button onClick={() => removeSub(idx)} className="text-[10px] text-red-600 hover:underline">Remove</button>
              {canFinance && sub.subcontractor_name && (
                <button onClick={() => onPay(sub, idx)} className="text-[10px] font-semibold text-indigo-700 hover:underline">Payment</button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
              <div><label className="text-[9px] uppercase text-slate-400">Our cost</label>
                <input type="number" step="0.01" value={sub.contract_amount || 0}
                  onChange={(e) => patchSub(idx, { contract_amount: parseFloat(e.target.value) || 0 })}
                  className="w-full rounded border border-slate-200 px-2 py-1 text-xs text-right" /></div>
              <div><label className="text-[9px] uppercase text-slate-400">Billed to client</label>
                <input type="number" step="0.01" value={sub.billed_to_client || 0}
                  onChange={(e) => patchSub(idx, { billed_to_client: parseFloat(e.target.value) || 0 })}
                  className="w-full rounded border border-slate-200 px-2 py-1 text-xs text-right" /></div>
              <div><label className="text-[9px] uppercase text-slate-400">Progress %</label>
                <input type="number" min="0" max="100" value={sub.progress_pct || 0}
                  onChange={(e) => patchSub(idx, { progress_pct: parseFloat(e.target.value) || 0 })}
                  className="w-full rounded border border-slate-200 px-2 py-1 text-xs text-right" /></div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-1 border-t border-slate-100">
        <button onClick={onClose} disabled={busy}
          className="text-xs px-2.5 py-1 rounded border border-slate-200 hover:bg-slate-50">Cancel</button>
        <button
          data-testid={`prj-wbs-phase-save-${phase.id}`}
          onClick={save} disabled={busy}
          className="text-xs px-3 py-1 rounded bg-emerald-600 text-white font-semibold inline-flex items-center gap-1"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save
        </button>
      </div>
    </div>
  );
}

/* ================= Quotes panel (up to 3 supplier quotes per material step) ================= */
function QuotesPanel({ project, scope, step, onDone, onCall }) {
  const [busy, setBusy] = useState(false);
  const [newQ, setNewQ] = useState({ supplier: "", unit_price: 0, notes: "" });
  const quotes = step.quotes || [];
  const path = `/projects/${project.id}/scopes/${scope.id}/steps/${step.id}/quotes`;

  const add = async () => {
    if (!newQ.supplier.trim()) { toast.error("Supplier is required"); return; }
    setBusy(true);
    try {
      await onCall(() => api.post(path, {
        supplier: newQ.supplier.trim(),
        unit_price: parseFloat(newQ.unit_price) || 0,
        notes: newQ.notes || "",
      }));
      setNewQ({ supplier: "", unit_price: 0, notes: "" });
    } finally { setBusy(false); }
  };

  const select = async (q) => {
    setBusy(true);
    try { await onCall(() => api.patch(`${path}/${q.id}`, { selected: true })); }
    finally { setBusy(false); }
  };

  const remove = async (q) => {
    if (!window.confirm(`Remove quote from ${q.supplier}?`)) return;
    setBusy(true);
    try { await onCall(() => api.del(`${path}/${q.id}`)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-12 gap-1 mb-2" data-testid={`prj-quotes-panel-${step.id}`}>
      <div className="col-span-12 bg-indigo-50/60 border border-indigo-200 rounded-lg p-2">
        <div className="text-[10px] font-bold uppercase text-indigo-800 mb-1.5 flex items-center justify-between">
          <span>Quotes · {quotes.length}/3</span>
          <button onClick={onDone} className="text-slate-500 hover:text-slate-700"><X className="w-3 h-3" /></button>
        </div>
        {quotes.map((q) => (
          <div key={q.id} className={`grid grid-cols-12 gap-1 items-center py-1 border-b border-indigo-100 last:border-0 ${q.selected ? "bg-emerald-50" : ""}`}>
            <div className="col-span-1 flex justify-center">
              <button
                data-testid={`prj-quote-select-${q.id}`}
                onClick={() => select(q)} disabled={busy}
                className={`w-4 h-4 rounded-full border-2 ${q.selected ? "bg-emerald-500 border-emerald-500" : "border-slate-300 hover:border-emerald-400"}`}
                title="Select this quote"
              />
            </div>
            <div className="col-span-4 text-xs font-semibold text-slate-900 truncate">{q.supplier}</div>
            <div className="col-span-2 text-right text-xs tabular-nums font-semibold">{money(q.unit_price)}</div>
            <div className="col-span-2 text-right text-xs tabular-nums text-slate-600">= {money(q.total)}</div>
            <div className="col-span-2 text-[10px] text-slate-500 truncate">{q.notes || "—"}</div>
            <button onClick={() => remove(q)}
              data-testid={`prj-quote-delete-${q.id}`}
              className="col-span-1 p-1 text-slate-400 hover:text-red-600 justify-self-end">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        {quotes.length < 3 && (
          <div className="grid grid-cols-12 gap-1 items-center pt-2 border-t border-indigo-100 mt-1">
            <div className="col-span-1"></div>
            <div className="col-span-4">
              <SupplierAutocomplete
                value={newQ.supplier}
                onChange={({ name }) => setNewQ({ ...newQ, supplier: name })}
                testId={`prj-quote-supplier-${step.id}`}
                placeholder="Supplier"
              />
            </div>
            <input type="number" step="0.01" placeholder="Price" value={newQ.unit_price}
              data-testid={`prj-quote-price-${step.id}`}
              onChange={(e) => setNewQ({ ...newQ, unit_price: e.target.value })}
              className="col-span-2 rounded border border-slate-200 px-2 py-1 text-xs text-right" />
            <input placeholder="Notes" value={newQ.notes}
              onChange={(e) => setNewQ({ ...newQ, notes: e.target.value })}
              className="col-span-4 rounded border border-slate-200 px-2 py-1 text-xs" />
            <button
              data-testid={`prj-quote-add-${step.id}`}
              onClick={add} disabled={busy || !newQ.supplier.trim()}
              className="col-span-1 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-bold px-2 py-1 disabled:opacity-50"
            >Add</button>
          </div>
        )}
      </div>
    </div>
  );
}
